import React, { useState, useEffect } from 'react';
import { createAuth } from './nuera-auth-client';

const authClient = createAuth('shield');

// ─── Plan Feature Gating ──────────────────────────────────────────────────
const PLAN_LEVELS = { starter: 1, pro: 2, professional: 2, enterprise: 3 };

const getUserPlan = (user) => {
  return user?.subscription?.plan_id?.toLowerCase() || 'starter';
};

const hasAccess = (user, requiredPlan) => {
  if (user?.email === 'hello@nuera.space') return true;
  const userPlan = getUserPlan(user);
  const userLevel = PLAN_LEVELS[userPlan] || 0;
  const requiredLevel = PLAN_LEVELS[requiredPlan] || 1;
  return userLevel >= requiredLevel;
};

// ─── Multi-Vertical Configuration ─────────────────────────────────────────
const BUSINESS_TYPES = {
  gmb: {
    id: 'gmb',
    label: 'Google My Business (GMB / Local)',
    iconName: 'Store',
    description: 'Local Business GMB rating integration (Restaurants, shops, salons)',
    reviewPlatform: 'Google',
    qrLabel: 'Rate Us on Google',
    setupFields: ['businessName', 'googleReviewLink', 'whatsappNumber'],
    plans: ['starter', 'pro', 'enterprise'],
    pricing: { starter: 299, pro: 699, enterprise: 1999 }
  },
  travels: {
    id: 'travels',
    label: 'Travels / Bus Operator',
    iconName: 'Bus',
    description: 'Bus services, travel agencies, transport companies',
    reviewPlatform: 'Google or Custom',
    qrLabel: 'Rate Your Journey',
    setupFields: ['businessName', 'googleReviewLink', 'whatsappNumber', 'buses'],
    multiUnit: true,
    unitLabel: 'Bus',
    maxUnits: { pro: 1, enterprise: 5 },
    plans: ['pro', 'enterprise'],
    pricing: { pro: 2999, enterprise: 4999 }
  },
  amazon: {
    id: 'amazon',
    label: 'Product Review (Amazon)',
    iconName: 'Package',
    description: 'Amazon FBA / FBM sellers, product reviews',
    reviewPlatform: 'Amazon',
    qrLabel: 'Rate This Product',
    setupFields: ['businessName', 'amazonProductLink', 'whatsappNumber', 'products'],
    multiUnit: true,
    unitLabel: 'Product',
    maxUnits: { pro: 1, enterprise: 5 },
    plans: ['pro', 'enterprise'],
    pricing: { pro: 1499, enterprise: 2999 }
  }
};

// Logo components using real brand assets (original colors)
const LogoIcon = ({ size = 36 }) => (
  <img src="/shield-icon.webp" alt="Shield.Nuera Icon" style={{ width: `${size}px`, height: `${size}px`, objectFit: 'contain', display: 'block' }} />
);

const LogoWordmark = ({ height = 36 }) => (
  <img src="/shield-logo.webp" alt="Shield.Nuera" style={{ height: `${height}px`, width: 'auto', objectFit: 'contain', display: 'block' }} />
);

// ─── Icons (inline SVG for zero-dependency) ──────────────────────────────────
const Icon = {
  Shield: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  Star: ({ filled }) => (
    <svg viewBox="0 0 24 24" fill={filled ? "#fbbf24" : "none"} stroke="#fbbf24" strokeWidth="1.5" width="24" height="24">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  ),
  LogOut: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  Copy: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  ),
  Check: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  Loader: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20" style={{animation:'spin 1s linear infinite'}}>
      <line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
    </svg>
  ),
  BarChart: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/>
    </svg>
  ),
  MessageSquare: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  Settings: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  Link: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
    </svg>
  ),
  Bell: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  ),
  Edit: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  X: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" width="16" height="16">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Save: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
    </svg>
  ),
  Zap: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  ),
  Google: () => (
    <svg viewBox="0 0 24 24" width="20" height="20">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  ),
  ArrowRight: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
  ),
  CheckCircle: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  ),
  TrendingUp: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
    </svg>
  ),
  Phone: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.7a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.6a16 16 0 0 0 6.29 6.29l1.92-1.94a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  ),
  Folder: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  ),
  Store: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
      <path d="M20 20a1 1 0 0 0 1-1V7.86a1 1 0 0 0-.47-.85l-8-5a1 1 0 0 0-1.06 0l-8 5A1 1 0 0 0 3 7.86V19a1 1 0 0 0 1 1h16z"/>
      <path d="M3 7h18"/>
      <path d="M10 20V14h4v6"/>
    </svg>
  ),
  Bus: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
      <rect x="2" y="6" width="20" height="12" rx="2" ry="2"/>
      <path d="M6 18h4"/>
      <path d="M14 18h4"/>
      <circle cx="7" cy="18" r="2"/>
      <circle cx="17" cy="18" r="2"/>
      <path d="M4 14h16"/>
      <path d="M2 10h20"/>
    </svg>
  ),
  Package: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/>
      <polygon points="12 22.08 12 12 3 6.92 3 17.08 12 22.08"/>
      <polygon points="12 22.08 21 17.08 21 6.92 12 12 12 22.08"/>
      <polygon points="12 12 21 6.92 12 1.84 3 6.92 12 12"/>
    </svg>
  ),
  Mail: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
};

// ─── Style injector ───────────────────────────────────────────────────────────
const S = {
  // Layout
  page: { minHeight: '100vh', background: 'var(--bg-primary)' },
  
  // Text helpers
  heading1: { fontSize: '32px', fontWeight: '800', color: 'var(--text-primary)', letterSpacing: '-0.02em', lineHeight: 1.2 },
  heading2: { fontSize: '22px', fontWeight: '700', color: 'var(--text-primary)', letterSpacing: '-0.01em' },
  heading3: { fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' },
  subtext: { fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6 },
  muted: { fontSize: '13px', color: 'var(--text-muted)' },
  label: { display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.08em' },
};

// ─── Spinner ──────────────────────────────────────────────────────────────────
const Spinner = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width={size} height={size}
    style={{ animation: 'spin 0.8s linear infinite', display: 'inline-block', verticalAlign: 'middle' }}>
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round"/>
  </svg>
);

// ─── Main App ─────────────────────────────────────────────────────────────────
const ShieldNueraApp = () => {
  const [currentPage, setCurrentPage] = useState('welcome');
  const [user, setUser] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [reviewLink, setReviewLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({ totalScans: 0, redirected5Star: 0, privateComplaints: 0, whatsappAlerts: 0 });
  const [privateReviews, setPrivateReviews] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [editForm, setEditForm] = useState({
    businessName: '', businessLocation: '', businessCategory: 'restaurant',
    customCategory: '', googleReviewUrl: '', ownerPhone: '', managerPhone: '',
    sorryMessage: ''
  });

  // Admin States
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminClients, setAdminClients] = useState([]);
  const [loadingAdmin, setLoadingAdmin] = useState(false);
  const [editingUserSub, setEditingUserSub] = useState(null);
  const [editSubForm, setEditSubForm] = useState({ planId: 'pro', status: 'active', durationDays: '30' });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://shild-backend-worker.sharathstash21.workers.dev';

  const fetchDashboardData = async (shouldRedirect = true) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const [profileRes, statsRes, reviewsRes] = await Promise.all([
        fetch(`${backendUrl}/api/business/profile`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${backendUrl}/api/dashboard/stats`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${backendUrl}/api/reviews/private`, { headers: { 'Authorization': `Bearer ${token}` } }),
      ]);

      if (profileRes.ok) {
        const profileData = await profileRes.json();
        if (profileData.success) {
          const biz = profileData.businessData;
          const knownCategories = ['restaurant', 'cafe', 'retail', 'hotel', 'salon', 'service', 'healthcare', 'fitness', 'automotive', 'education', 'realestate'];
          const isKnown = knownCategories.includes(biz?.businessCategory);
          const initialCategory = isKnown ? biz.businessCategory : 'other';
          const initialCustom = isKnown ? '' : (biz?.businessCategory || '');

          const formattedBiz = {
            ...biz,
            businessCategory: initialCategory,
            customCategory: initialCustom,
            sorryMessage: biz?.sorryMessage || ''
          };

          setBusinessData(formattedBiz);
          setEditForm(formattedBiz);
          setReviewLink(`${window.location.origin}/review?id=${profileData.clientId}`);
          
          // Fetch units if travels or amazon
          if (biz?.businessType === 'travels' || biz?.businessType === 'amazon') {
            try {
              const unitsRes = await fetch(`${backendUrl}/api/business/units`, { headers: { 'Authorization': `Bearer ${token}` } });
              if (unitsRes.ok) {
                const uData = await unitsRes.json();
                setBusinessUnits(uData.units || []);
              }
            } catch (unitErr) {
              console.error('Failed to fetch business units:', unitErr);
            }
          }
          
          if (shouldRedirect) {
            setCurrentPage('dashboard');
          }
        }
      } else if (profileRes.status === 404) {
        if (shouldRedirect) {
          setCurrentPage('onboarding');
        }
      }

      if (statsRes.ok) setStats(await statsRes.json());
      if (reviewsRes.ok) {
        const d = await reviewsRes.json();
        setPrivateReviews(d.reviews || []);
      }
    } catch (err) {
      console.error('Dashboard data error:', err);
    }
  };

  const fetchAdminData = async () => {
    try {
      setLoadingAdmin(true);
      const token = localStorage.getItem('authToken');
      const [usersRes, clientsRes] = await Promise.all([
        fetch('https://nuera-central-auth.sharathstash21.workers.dev/auth/admin/users', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${backendUrl}/api/admin/clients`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      if (usersRes.ok) {
        const data = await usersRes.json();
        setAdminUsers(data.users || []);
      }
      if (clientsRes.ok) {
        const data = await clientsRes.json();
        setAdminClients(data.clients || []);
      }
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
    } finally {
      setLoadingAdmin(false);
    }
  };

  const handleUpdateSubscriptionSubmit = async (e) => {
    e.preventDefault();
    if (!editingUserSub) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const res = await fetch('https://nuera-central-auth.sharathstash21.workers.dev/auth/admin/subscriptions/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          userId: editingUserSub.id,
          planId: editSubForm.planId,
          status: editSubForm.status,
          durationDays: editSubForm.durationDays
        })
      });
      if (res.ok) {
        alert('Subscription updated successfully!');
        setEditingUserSub(null);
        await fetchAdminData();
      } else {
        alert('Failed to update subscription');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    try {
      setLoading(true);
      const result = await authClient.signIn();
      if (result?.user) {
        const token = authClient.getToken();
        localStorage.setItem('authToken', token);
        const shieldSub = result.subscriptions?.find(s => s.product_slug === 'shield');
        const hasPaid = !!shieldSub && shieldSub.status === 'active';
        setUser({ ...result.user, isPaidSubscriber: hasPaid, subscription: shieldSub });
        if (hasPaid) await fetchDashboardData();
        else setCurrentPage('payment');
      }
    } catch (err) {
      alert('Authentication failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    authClient.getMe().then(data => {
      if (data?.user) {
        const token = authClient.getToken();
        localStorage.setItem('authToken', token);
        const shieldSub = data.subscriptions?.find(s => s.product_slug === 'shield');
        const hasPaid = !!shieldSub && shieldSub.status === 'active';
        setUser({ ...data.user, isPaidSubscriber: hasPaid, subscription: shieldSub });
        if (hasPaid) fetchDashboardData(false); // Load details in background, stay on welcome/landing page
      }
    }).catch(err => console.error(err));
  }, []);

  const handleLogout = () => {
    authClient.signOut?.();
    localStorage.removeItem('authToken');
    setUser(null);
    setBusinessData(null);
    setCurrentPage('welcome');
  };

  const handleRazorpayPayment = async (planId, verticalId = 'gmb') => {
    try {
      setLoading(true);
      const vertical = BUSINESS_TYPES[verticalId] || BUSINESS_TYPES.gmb;
      const price = vertical.pricing[planId] || 299;
      const amount = price * 100;
      const planNames = { starter: 'Starter', pro: 'Professional', enterprise: 'Enterprise' };

      // Try to get a real order ID from backend
      let realOrderId = null;
      try {
        const res = await fetch(`${backendUrl}/api/payment/create-order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
          body: JSON.stringify({ planId, amount, userId: user?.id || user?.email })
        });
        if (res.ok) {
          const data = await res.json();
          // Only use it if it's a real Razorpay order ID
          if (data.orderId && data.orderId.startsWith('order_')) {
            realOrderId = data.orderId;
          }
        }
      } catch { /* backend may not have payment endpoint, that's fine */ }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount,
        currency: 'INR',
        name: 'Shield.Nuera',
        description: `${planNames[planId]} Plan (${vertical.label}) — Monthly Subscription`,
        image: '/shield-icon.webp',
        handler: async (resp) => {
          // Payment successful — activate subscription
          try {
            await fetch(`${backendUrl}/api/payment/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
              body: JSON.stringify({ orderId: resp.razorpay_order_id, paymentId: resp.razorpay_payment_id, signature: resp.razorpay_signature, planId })
            });
          } catch { /* verification optional in test mode */ }
          
          // Save selected vertical to localStorage so onboarding knows it
          localStorage.setItem('selectedVertical', verticalId);
          
          // Sync with the backend & reload updated subscription
          try {
            const data = await authClient.getMe();
            if (data?.user) {
              const shieldSub = data.subscriptions?.find(s => s.product_slug === 'shield');
              const hasPaid = !!shieldSub && shieldSub.status === 'active';
              setUser({ ...data.user, isPaidSubscriber: hasPaid, subscription: shieldSub });
            } else {
              setUser(prev => ({
                ...prev,
                isPaidSubscriber: true,
                plan: planId,
                subscription: { ...prev?.subscription, plan_id: planId, status: 'active' }
              }));
            }
          } catch {
            setUser(prev => ({
              ...prev,
              isPaidSubscriber: true,
              plan: planId,
              subscription: { ...prev?.subscription, plan_id: planId, status: 'active' }
            }));
          }

          if (!businessData) {
            setCurrentPage('onboarding');
          }
          setShowUpgradeModal(false);
          setLoading(false);
        },
        prefill: { email: user?.email || '', name: user?.name || '' },
        notes: { planId, verticalId, userId: user?.id || user?.email },
        theme: { color: '#00ff87' },
        modal: {
          ondismiss: () => setLoading(false)
        }
      };

      // Only add order_id if backend gave us a real one — never pass fake IDs
      if (realOrderId) {
        options.order_id = realOrderId;
      }

      if (window.Razorpay) {
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', () => setLoading(false));
        rzp.open();
      } else {
        // No Razorpay SDK — simulate success for local dev
        localStorage.setItem('selectedVertical', verticalId);
        setUser(prev => ({
          ...prev,
          isPaidSubscriber: true,
          plan: planId,
          subscription: {
            ...prev?.subscription,
            plan_id: planId,
            status: 'active'
          }
        }));
        if (!businessData) {
          setCurrentPage('onboarding');
        }
        setShowUpgradeModal(false);
        setLoading(false);
      }
    } catch (err) {
      alert('Payment error: ' + err.message);
      setLoading(false);
    }
  };

  const handleBusinessSetup = async (formPayload) => {
    try {
      setLoading(true);
      const payload = {
        ...formPayload,
        businessCategory: formPayload.businessCategory === 'other' ? formPayload.customCategory : formPayload.businessCategory
      };
      const res = await fetch(`${backendUrl}/api/business/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        const biz = data.businessData;
        const knownCategories = ['restaurant', 'cafe', 'retail', 'hotel', 'salon', 'service', 'healthcare', 'fitness', 'automotive', 'education', 'realestate'];
        const isKnown = knownCategories.includes(biz?.businessCategory);
        const initialCategory = isKnown ? biz.businessCategory : 'other';
        const initialCustom = isKnown ? '' : (biz?.businessCategory || '');

        const formattedBiz = {
          ...biz,
          businessCategory: initialCategory,
          customCategory: initialCustom,
          sorryMessage: biz?.sorryMessage || ''
        };

        setBusinessData(formattedBiz);
        setEditForm(formattedBiz);
        await fetchDashboardData();
      } else {
        alert('Setup failed. Please try again.');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {
        ...editForm,
        businessCategory: editForm.businessCategory === 'other' ? editForm.customCategory : editForm.businessCategory
      };
      const res = await fetch(`${backendUrl}/api/business/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        const biz = data.businessData;
        const knownCategories = ['restaurant', 'cafe', 'retail', 'hotel', 'salon', 'service', 'healthcare', 'fitness', 'automotive', 'education', 'realestate'];
        const isKnown = knownCategories.includes(biz?.businessCategory);
        const initialCategory = isKnown ? biz.businessCategory : 'other';
        const initialCustom = isKnown ? '' : (biz?.businessCategory || '');

        const formattedBiz = {
          ...biz,
          businessCategory: initialCategory,
          customCategory: initialCustom,
          sorryMessage: biz?.sorryMessage || ''
        };

        setBusinessData(formattedBiz);
        setEditForm(formattedBiz);
        setIsEditing(false);
      } else {
        alert('Failed to save settings.');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleAddUnit = async (label, reviewLink) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${backendUrl}/api/business/units`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ unitLabel: label, unitType: businessData?.businessType === 'amazon' ? 'product' : 'bus', reviewLink })
      });
      if (res.ok) {
        const data = await res.json();
        setBusinessUnits(prev => [data.unit, ...prev]);
        return { success: true };
      } else {
        const error = await res.json();
        return { success: false, error: error.error || 'Failed to add unit.' };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const handleDeleteUnit = async (unitId) => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${backendUrl}/api/business/units?id=${unitId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setBusinessUnits(prev => prev.filter(u => u.id !== unitId));
        return { success: true };
      } else {
        const error = await res.json();
        return { success: false, error: error.error || 'Failed to delete unit.' };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Mouse position tracker for cursor glow
  useEffect(() => {
    const handleMouseMove = (e) => {
      const glow = document.getElementById('cursor-glow');
      if (glow) {
        glow.style.left = `${e.clientX}px`;
        glow.style.top = `${e.clientY}px`;
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // ─── Customer review page ─────────────────────────────────────────────────
  if (window.location.pathname.includes('/review')) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', background: '#020603', overflowX: 'hidden' }}>
        <div className="grain"></div>
        <div className="cursor-glow" id="cursor-glow"></div>
        <CustomerReviewPage backendUrl={backendUrl} />
      </div>
    );
  }

  const renderCurrentPage = () => {
    if (currentPage === 'welcome' || !user) {
      return (
        <WelcomePage
          onAuth={handleGoogleAuth}
          loading={loading}
          user={user}
          onEnterDashboard={() => {
            if (!user.isPaidSubscriber) {
              setCurrentPage('payment');
            } else if (!businessData) {
              fetchDashboardData(true);
            } else {
              setCurrentPage('dashboard');
            }
          }}
        />
      );
    }
    if (!user.isPaidSubscriber) {
      return (
        <PaymentPage
          user={user}
          loading={loading}
          onPay={handleRazorpayPayment}
          onLogout={handleLogout}
          onGoHome={() => setCurrentPage('welcome')}
        />
      );
    }
    if (!businessData) {
      return (
        <OnboardingPage
          user={user}
          loading={loading}
          onSetup={handleBusinessSetup}
          onLogout={handleLogout}
          onGoHome={() => setCurrentPage('welcome')}
        />
      );
    }

    return (
      <DashboardPage
        user={user}
        businessData={businessData}
        reviewLink={reviewLink}
        stats={stats}
        privateReviews={privateReviews}
        businessUnits={businessUnits}
        onAddUnit={handleAddUnit}
        onDeleteUnit={handleDeleteUnit}
        copied={copied}
        setCopied={setCopied}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        editForm={editForm}
        setEditForm={setEditForm}
        savingSettings={savingSettings}
        onSaveSettings={handleSaveSettings}
        onLogout={handleLogout}
        loading={loading}
        onUpgradeClick={() => setShowUpgradeModal(true)}
        onGoHome={() => setCurrentPage('welcome')}
        // Admin Props
        adminUsers={adminUsers}
        adminClients={adminClients}
        loadingAdmin={loadingAdmin}
        editingUserSub={editingUserSub}
        setEditingUserSub={setEditingUserSub}
        editSubForm={editSubForm}
        setEditSubForm={setEditSubForm}
        onUpdateSubscriptionSubmit={handleUpdateSubscriptionSubmit}
        fetchAdminData={fetchAdminData}
      />
    );
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', background: '#020603', overflowX: 'hidden' }}>
      <div className="grain"></div>
      <div className="cursor-glow" id="cursor-glow"></div>
      {renderCurrentPage()}
      {showUpgradeModal && (
        <UpgradeModal
          user={user}
          businessData={businessData}
          loading={loading}
          onClose={() => setShowUpgradeModal(false)}
          onPay={(planId) => {
            const verticalId = businessData?.businessType || 'gmb';
            handleRazorpayPayment(planId, verticalId);
          }}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// WELCOME PAGE
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// WELCOME PAGE
// ─────────────────────────────────────────────────────────────────────────────
const WelcomePage = ({ onAuth, loading, user, onEnterDashboard }) => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="emerald-welcome-page">
      <style>{`
        :root {
          --bg-void: #050806;
          --bg-void-2: #070c09;
          --bg-panel: rgba(14, 24, 18, 0.55);
          --bg-panel-solid: #0c1611;
          --emerald-core: #00ffa0;
          --emerald-bright: #39ffb0;
          --emerald-deep: #063a26;
          --emerald-dim: #0a2a1c;
          --ring-line: rgba(0, 255, 160, 0.35);
          --text-primary: #eafff5;
          --text-secondary: #8fb0a1;
          --text-tertiary: #547164;
          --danger: #ff5470;
          --warn: #ffb547;
        }
        
        .emerald-welcome-page {
          background:
            radial-gradient(ellipse 900px 600px at 50% -10%, rgba(0,255,160,0.10), transparent 60%),
            radial-gradient(ellipse 700px 500px at 85% 30%, rgba(0,255,160,0.05), transparent 60%),
            linear-gradient(180deg, var(--bg-void) 0%, var(--bg-void-2) 100%);
          min-height: 100vh;
          color: var(--text-primary);
          font-family: 'Inter', sans-serif;
          overflow-x: hidden;
          position: relative;
          padding-top: 86px;
        }
        
        .emerald-welcome-page a {
          color: inherit;
          text-decoration: none;
        }
        
        .emerald-welcome-page .eyebrow {
          font-family: 'Orbitron', sans-serif;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.25em;
          text-transform: uppercase;
          color: var(--emerald-core);
        }
        
        /* ---------- NAV ---------- */
        .emerald-welcome-page nav {
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          padding: 16px 5vw !important;
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          z-index: 9999 !important;
          gap: 20px !important;
          background: rgba(5, 8, 6, 0.75) !important;
          backdrop-filter: blur(18px) saturate(160%) !important;
          -webkit-backdrop-filter: blur(18px) saturate(160%) !important;
          border-bottom: 1px solid rgba(0, 255, 160, 0.12) !important;
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.03) !important;
        }
        
        .emerald-welcome-page .logo-lockup {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-shrink: 0;
        }
        
        .emerald-welcome-page .logo-text {
          font-family: 'Space Grotesk', sans-serif;
          font-weight: 700;
          font-size: 19px;
          letter-spacing: 0.02em;
          color: var(--text-primary);
        }
        
        .emerald-welcome-page .logo-text .dim {
          color: var(--text-tertiary);
          font-weight: 500;
        }
        
        .emerald-welcome-page .nav-links {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 36px;
          font-size: 14px;
          color: var(--text-secondary);
          white-space: nowrap;
          flex: 1;
        }
        
        .emerald-welcome-page .nav-links a {
          transition: color 0.2s ease;
          white-space: nowrap;
        }
        
        .emerald-welcome-page .nav-links a:hover {
          color: var(--emerald-bright);
        }
        
        .emerald-welcome-page .nav-cta {
          border: 1px solid var(--ring-line);
          padding: 9px 20px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          color: var(--text-primary);
          transition: all .25s ease;
          cursor: pointer;
          background: transparent;
        }
        
        .emerald-welcome-page .nav-cta:hover:not(:disabled) {
          border-color: var(--emerald-core);
          box-shadow: 0 0 18px rgba(0,255,160,0.25);
        }
        
        .emerald-welcome-page .nav-cta:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        /* ---------- HERO ---------- */
        .emerald-welcome-page .hero {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 60px 20px 40px;
          position: relative;
        }
        
        .emerald-welcome-page .ring-stage {
          position: relative;
          width: 320px;
          height: 320px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 18px;
        }
        
        .emerald-welcome-page .ring {
          position: absolute;
          border-radius: 50%;
          border: 1px solid var(--ring-line);
        }
        
        .emerald-welcome-page .ring1 {
          width: 100%;
          height: 100%;
          animation: spin-clockwise 22s linear infinite;
          border-style: dashed;
          opacity: 0.5;
        }
        
        .emerald-welcome-page .ring2 {
          width: 78%;
          height: 78%;
          animation: spin-counter 16s linear infinite;
          opacity: 0.7;
        }
        
        .emerald-welcome-page .ring3 {
          width: 56%;
          height: 56%;
          animation: pulse-ring 3.2s ease-in-out infinite;
        }
        
        @keyframes spin-clockwise {
          to { transform: rotate(360deg); }
        }
        
        @keyframes spin-counter {
          to { transform: rotate(-360deg); }
        }
        
        @keyframes pulse-ring {
          0%, 100% {
            box-shadow: 0 0 0px rgba(0,255,160,0.0);
            border-color: var(--ring-line);
          }
          50% {
            box-shadow: 0 0 40px rgba(0,255,160,0.35);
            border-color: var(--emerald-core);
          }
        }
        
        .emerald-welcome-page .emblem {
          position: relative;
          width: 120px;
          height: 120px;
          z-index: 2;
          animation: emblem-breathe 3.2s ease-in-out infinite;
        }
        
        @keyframes emblem-breathe {
          0%, 100% {
            filter: drop-shadow(0 0 14px rgba(0,255,160,0.55)) drop-shadow(0 0 34px rgba(0,255,160,0.25));
          }
          50% {
            filter: drop-shadow(0 0 26px rgba(0,255,160,0.85)) drop-shadow(0 0 54px rgba(0,255,160,0.4));
          }
        }
        
        .emerald-welcome-page .particle {
          position: absolute;
          bottom: 0;
          width: 3px;
          height: 3px;
          border-radius: 50%;
          background: var(--emerald-core);
          box-shadow: 0 0 8px 2px rgba(0,255,160,0.8);
          animation: rise 6s linear infinite;
          opacity: 0;
        }
        
        @keyframes rise {
          0% { transform: translateY(0) scale(1); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 0.6; }
          100% { transform: translateY(-340px) scale(0.3); opacity: 0; }
        }
        
        .emerald-welcome-page h1 {
          font-family: 'Space Grotesk', sans-serif;
          font-size: clamp(34px, 5vw, 58px);
          font-weight: 700;
          line-height: 1.08;
          letter-spacing: -0.01em;
          margin-top: 6px;
          max-width: 800px;
          color: var(--text-primary);
        }
        
        .emerald-welcome-page h1 .glow-word {
          color: var(--emerald-core);
          text-shadow: 0 0 22px rgba(0,255,160,0.65), 0 0 60px rgba(0,255,160,0.25);
        }
        
        .emerald-welcome-page .hero p.sub {
          margin-top: 18px;
          max-width: 520px;
          color: var(--text-secondary);
          font-size: 16px;
          line-height: 1.6;
        }
        
        .emerald-welcome-page .hero-ctas {
          display: flex;
          gap: 14px;
          margin-top: 32px;
        }
        
        .emerald-welcome-page .btn-primary-emerald {
          background: linear-gradient(180deg, var(--emerald-bright), var(--emerald-core));
          color: #04150d;
          font-weight: 600;
          font-size: 14px;
          padding: 14px 28px;
          border-radius: 8px;
          box-shadow: 0 0 24px rgba(0,255,160,0.35), inset 0 1px 0 rgba(255,255,255,0.3);
          transition: transform .2s ease, box-shadow .2s ease;
          border: none;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        
        .emerald-welcome-page .btn-primary-emerald:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 0 36px rgba(0,255,160,0.55);
        }
        
        .emerald-welcome-page .btn-primary-emerald:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          box-shadow: none;
        }
        
        .emerald-welcome-page .btn-ghost {
          border: 1px solid var(--ring-line);
          padding: 14px 24px;
          border-radius: 8px;
          font-size: 14px;
          color: var(--text-primary);
          transition: all .2s ease;
          cursor: pointer;
          background: transparent;
        }
        
        .emerald-welcome-page .btn-ghost:hover {
          border-color: var(--emerald-core);
          background: rgba(0,255,160,0.06);
        }
        
        /* ---------- CHARGE METER ---------- */
        .emerald-welcome-page .charge-section {
          max-width: 980px;
          margin: 90px auto 0;
          padding: 0 20px;
        }
        
        .emerald-welcome-page .charge-card {
          background: var(--bg-panel);
          border: 1px solid var(--ring-line);
          border-radius: 16px;
          padding: 32px 36px;
          backdrop-filter: blur(14px);
          display: flex;
          align-items: center;
          gap: 32px;
          flex-wrap: wrap;
        }
        
        .emerald-welcome-page .charge-label {
          flex: 1;
          min-width: 220px;
        }
        
        .emerald-welcome-page .charge-label h3 {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 20px;
          margin-bottom: 6px;
          color: var(--text-primary);
        }
        
        .emerald-welcome-page .charge-label p {
          color: var(--text-secondary);
          font-size: 14px;
        }
        
        .emerald-welcome-page .charge-meter {
          flex: 2;
          min-width: 280px;
        }
        
        .emerald-welcome-page .meter-track {
          position: relative;
          height: 10px;
          border-radius: 6px;
          background: var(--bg-panel-solid);
          border: 1px solid var(--ring-line);
          overflow: hidden;
        }
        
        .emerald-welcome-page .meter-fill {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 82%;
          border-radius: 6px;
          background: linear-gradient(90deg, var(--emerald-deep), var(--emerald-core));
          box-shadow: 0 0 16px rgba(0,255,160,0.6);
          animation: fill-pulse 2.6s ease-in-out infinite;
        }
        
        @keyframes fill-pulse {
          0%, 100% { box-shadow: 0 0 12px rgba(0,255,160,0.4); }
          50% { box-shadow: 0 0 22px rgba(0,255,160,0.75); }
        }
        
        .emerald-welcome-page .meter-ticks {
          display: flex;
          justify-content: space-between;
          margin-top: 10px;
          font-family: 'Orbitron', sans-serif;
          font-size: 10px;
          letter-spacing: 0.12em;
          color: var(--text-tertiary);
        }
        
        .emerald-welcome-page .meter-ticks span.active {
          color: var(--emerald-core);
        }
        
        /* ---------- FEATURE PANELS ---------- */
        .emerald-welcome-page .section-head {
          text-align: center;
          max-width: 600px;
          margin: 120px auto 50px;
          padding: 0 20px;
        }
        
        .emerald-welcome-page .section-head .eyebrow {
          display: block;
          margin-bottom: 12px;
        }
        
        .emerald-welcome-page .section-head h2 {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 34px;
          font-weight: 700;
          color: var(--text-primary);
        }
        
        .emerald-welcome-page .section-head p {
          color: var(--text-secondary);
          margin-top: 12px;
          font-size: 15px;
        }
        
        .emerald-welcome-page .panels {
          max-width: 1080px;
          margin: 0 auto;
          padding: 0 20px 100px;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 22px;
        }
        
        .emerald-welcome-page .panel {
          background: var(--bg-panel);
          border: 1px solid var(--ring-line);
          border-radius: 14px;
          padding: 28px;
          backdrop-filter: blur(14px);
          position: relative;
          overflow: hidden;
          transition: border-color .25s ease, transform .25s ease;
        }
        
        .emerald-welcome-page .panel::before {
          content: '';
          position: absolute;
          inset: -1px;
          border-radius: 14px;
          padding: 1px;
          background: linear-gradient(135deg, rgba(0,255,160,0.5), transparent 40%);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          opacity: 0;
          transition: opacity .3s ease;
          pointer-events: none;
        }
        
        .emerald-welcome-page .panel:hover {
          transform: translateY(-4px);
          border-color: rgba(0,255,160,0.55);
        }
        
        .emerald-welcome-page .panel:hover::before {
          opacity: 1;
        }
        
        .emerald-welcome-page .panel-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: var(--emerald-dim);
          border: 1px solid var(--ring-line);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          color: var(--emerald-core);
          font-family: 'Orbitron', sans-serif;
          font-weight: 700;
          font-size: 16px;
        }
        
        .emerald-welcome-page .panel h4 {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 17px;
          margin-bottom: 10px;
          color: var(--text-primary);
        }
        
        .emerald-welcome-page .panel p {
          color: var(--text-secondary);
          font-size: 13.5px;
          line-height: 1.6;
        }
        
        .emerald-welcome-page .panel .tag {
          display: inline-block;
          margin-top: 16px;
          font-family: 'Orbitron', sans-serif;
          font-size: 10px;
          letter-spacing: 0.1em;
          color: var(--text-tertiary);
          border-top: 1px solid var(--ring-line);
          padding-top: 14px;
          width: 100%;
        }
        
        .emerald-welcome-page .panel .tag b {
          color: var(--emerald-core);
        }
        
        .emerald-welcome-page footer {
          text-align: center;
          padding: 40px 20px 60px;
          color: var(--text-tertiary);
          font-size: 12px;
          border-top: 1px solid rgba(255,255,255,0.05);
          margin-top: 40px;
        }
        
        @media(max-width: 800px) {
          .emerald-welcome-page .panels {
            grid-template-columns: 1fr;
          }
          .emerald-welcome-page .nav-links {
            display: none;
          }
        }
      `}</style>

      <nav>
        <div className="logo-lockup">
          <LogoWordmark height={34} />
        </div>
        <div className="nav-links">
          <a href="#product" onClick={e => { e.preventDefault(); scrollTo('product'); }}>Product</a>
          <a href="#feedback" onClick={e => { e.preventDefault(); scrollTo('feedback'); }}>Feedback</a>
          <a href="#how-it-works" onClick={e => { e.preventDefault(); scrollTo('how-it-works'); }}>How it works</a>
        </div>
        {user ? (
          <button className="nav-cta" onClick={onEnterDashboard}>
            Go to Dashboard
          </button>
        ) : (
          <button className="nav-cta" onClick={onAuth} disabled={loading}>
            {loading ? <Spinner size={16} /> : 'Sign in'}
          </button>
        )}
      </nav>

      <section className="hero" id="product">
        <div className="ring-stage">
          <div className="ring ring1"></div>
          <div className="ring ring2"></div>
          <div className="ring ring3"></div>
          <img src="/shield-icon.webp" alt="Shield.Nuera Logo Icon" className="emblem" style={{ objectFit: 'contain', padding: '10px' }} />
          <div className="particle" style={{ left: '20%', animationDelay: '0s' }}></div>
          <div className="particle" style={{ left: '70%', animationDelay: '1.4s' }}></div>
          <div className="particle" style={{ left: '45%', animationDelay: '2.8s' }}></div>
          <div className="particle" style={{ left: '85%', animationDelay: '4.2s' }}></div>
          <div className="particle" style={{ left: '10%', animationDelay: '5.5s' }}></div>
        </div>

        <span className="eyebrow">Enterprise Reputation Management</span>
        <h1 style={{ marginTop: '14px' }}>Charge up your <span className="glow-word">5-star</span> reputation</h1>
        <p className="sub">Every review gets screened at the source. Happy customers get routed to Google. Unhappy ones reach you first — privately, instantly, before it ever goes public.</p>

        <div className="hero-ctas">
          {user ? (
            <button className="btn-primary-emerald" onClick={onEnterDashboard}>
              Go to Dashboard →
            </button>
          ) : (
            <button className="btn-primary-emerald" onClick={onAuth} disabled={loading}>
              {loading ? <Spinner size={16} /> : <Icon.Google />}
              {loading ? 'Activating...' : 'Activate Shield →'}
            </button>
          )}
          <a href="#how-it-works" className="btn-ghost" onClick={e => { e.preventDefault(); scrollTo('how-it-works'); }}>See it in action</a>
        </div>
      </section>

      {/* ── STATS STRIP ──────────────────────────────────────────────── */}
      <div style={{ maxWidth: '1080px', margin: '64px auto 0', padding: '0 20px' }}>
        <div className="stats-grid-4" style={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(0,255,160,0.15)' }}>
          {[
            { num: '240+', label: 'Businesses Protected', icon: <svg viewBox="0 0 24 24" fill="none" stroke="#00ffa0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> },
            { num: '11,400+', label: 'Reviews Intercepted', icon: <svg viewBox="0 0 24 24" fill="none" stroke="#00ffa0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg> },
            { num: '4.8★', label: 'Avg Google Rating After', icon: <svg viewBox="0 0 24 24" fill="none" stroke="#00ffa0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> },
            { num: '₹1.2 Cr+', label: 'Revenue Saved from Bad PR', icon: <svg viewBox="0 0 24 24" fill="none" stroke="#00ffa0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> },
          ].map((s, i) => (
            <div key={i} style={{ background: 'rgba(14,24,18,0.55)', backdropFilter: 'blur(14px)', padding: '28px 20px', textAlign: 'center', borderRight: i < 3 ? '1px solid rgba(0,255,160,0.08)' : 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px', opacity: 0.85 }}>{s.icon}</div>
              <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '28px', fontWeight: '800', color: '#00ffa0', letterSpacing: '-0.02em', lineHeight: 1 }}>{s.num}</div>
              <div style={{ fontSize: '12px', color: '#547164', marginTop: '6px', fontWeight: '500' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <section className="charge-section" id="live-protection">
        <div className="charge-card">
          <div className="charge-label">
            <span className="eyebrow">Live Protection Level</span>
            <h3 style={{ marginTop: '10px' }}>Test Cafe & Lounge</h3>
            <p>82% of incoming reviews intercepted before reaching Google.</p>
          </div>
          <div className="charge-meter">
            <div className="meter-track"><div className="meter-fill"></div></div>
            <div className="meter-ticks">
              <span>UNPROTECTED</span>
              <span className="active">SHIELDED</span>
              <span>FULL CHARGE</span>
            </div>
          </div>
        </div>
      </section>

      <div className="section-head" id="how-it-works">
        <span className="eyebrow">How the shield works</span>
        <h2>Three checkpoints. Zero surprises.</h2>
        <p>The ring activates the moment a customer scans your code — before a single word reaches the public internet.</p>
      </div>

      <div className="panels">
        <div className="panel">
          <div className="panel-icon">1–4</div>
          <h4>Intercept quietly</h4>
          <p>Ratings below 5 stars are routed to a private feedback form — never to Google, never public.</p>
          <div className="tag">ROUTES TO&nbsp; <b>PRIVATE INBOX</b></div>
        </div>
        <div className="panel">
          <div className="panel-icon">★5</div>
          <h4>Redirect happy customers</h4>
          <p>5-star reviewers are sent straight to your Google Maps listing in one tap — no friction, no delay.</p>
          <div className="tag">ROUTES TO&nbsp; <b>GOOGLE MAPS</b></div>
        </div>
        <div className="panel">
          <div className="panel-icon">⚡</div>
          <h4>Alert the owner instantly</h4>
          <p>Every intercepted complaint pings you and your manager on WhatsApp the second it's submitted.</p>
          <div className="tag">DELIVERS TO&nbsp; <b>WHATSAPP</b></div>
        </div>
      </div>

      {/* ── TESTIMONIALS ─────────────────────────────────────────────── */}
      <div id="feedback" style={{ maxWidth: '1080px', margin: '0 auto', padding: '0 20px 20px' }}>
        <div className="section-head" style={{ margin: '90px auto 48px' }}>
          <span className="eyebrow">What business owners say</span>
          <h2 style={{ marginTop: '12px' }}>Real results. Real businesses.</h2>
          <p>Over 240 businesses across India trust Shield.Nuera to protect their online reputation every day.</p>
        </div>

        <div className="testimonials-grid">
          {[
            { name: 'Karthik Subramanian', initials: 'KS', role: 'Owner, Spice Garden Restaurant · Bangalore', text: 'Before Shield, one bad review could ruin a whole week. Now unhappy customers reach me directly on WhatsApp — I fix the issue before it goes public. Our Google rating went from 3.8 to 4.6 in just 3 months.', badge: '4.6★ Google now' },
            { name: 'Meenakshi Venkataraman', initials: 'MV', role: 'Co-founder, Aura Wellness Studio · Kochi', text: 'We run a beauty salon and one negative post used to kill our bookings. Shield intercepts the unhappy ones, I call them back, and they end up being our most loyal customers. Excellent product.', badge: '89% complaints resolved' },
            { name: 'Rohan Desai', initials: 'RD', role: 'GM, The Residency Suites · Pune', text: 'Hospitality lives and dies by reviews. Shield gives me a 24-hour head start on every complaint. Our TripAdvisor score and Google ranking both climbed within 60 days of going live.', badge: 'Ranking ↑ 9 positions' },
            { name: 'Lakshmi Narayanan', initials: 'LN', role: 'Director, FlexFit Gym · Coimbatore', text: 'My staff was nervous about bad reviews after we onboarded new trainers. Shield caught every complaint privately. Members felt heard, stayed subscribed, and we got better from the feedback.', badge: '0 public complaints' },
            { name: 'Siddharth Kulkarni', initials: 'SK', role: 'Owner, Precision Auto Works · Mumbai', text: 'Mechanics get blamed for everything. Shield lets me see exactly what went wrong before it hits Google. I resolved 31 complaints in 2 months — every customer leaves satisfied.', badge: '31 complaints resolved' },
            { name: 'Anitha Ramachandran', initials: 'AR', role: 'Principal, Vidyasagar Academy · Chennai', text: 'Parents are very vocal when unhappy. With Shield, I get notified the moment a parent rates us low and I call them personally. Our parent satisfaction score is at an all-time high.', badge: 'Parent sat. ↑ 34%' },
          ].map((t, i) => (
            <div key={i}
              style={{ background: 'rgba(14,24,18,0.55)', border: '1px solid rgba(0,255,160,0.12)', borderRadius: '14px', padding: '24px', backdropFilter: 'blur(14px)', display: 'flex', flexDirection: 'column', gap: '14px', transition: 'border-color .25s ease, transform .25s ease', cursor: 'default' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0,255,160,0.4)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(0,255,160,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{ display: 'flex', gap: '2px' }}>{'★★★★★'.split('').map((s, j) => <span key={j} style={{ color: '#fbbf24', fontSize: '14px' }}>{s}</span>)}</div>
              <p style={{ color: '#a8c8b8', fontSize: '13.5px', lineHeight: '1.65', margin: 0, flex: 1 }}>"{t.text}"</p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(0,255,160,0.08)', border: '1px solid rgba(0,255,160,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', color: '#00ffa0', flexShrink: 0, fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '0.05em' }}>{t.initials}</div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#eafff5' }}>{t.name}</div>
                    <div style={{ fontSize: '11px', color: '#547164' }}>{t.role}</div>
                  </div>
                </div>
                <div style={{ background: 'rgba(0,255,160,0.08)', border: '1px solid rgba(0,255,160,0.2)', borderRadius: '20px', padding: '3px 10px', fontSize: '11px', fontWeight: '700', color: '#00ffa0', whiteSpace: 'nowrap' }}>{t.badge}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── FINAL CTA ────────────────────────────────────────────────── */}
      <div style={{ textAlign: 'center', padding: '80px 20px 80px', marginTop: '60px', borderTop: '1px solid rgba(0,255,160,0.07)', background: 'radial-gradient(ellipse 600px 300px at 50% 0%, rgba(0,255,160,0.06), transparent)' }}>
        <span className="eyebrow" style={{ display: 'block', marginBottom: '16px' }}>Start protecting today</span>
        <h2 style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 'clamp(26px, 4vw, 42px)', fontWeight: '700', color: '#eafff5', margin: '0 auto 14px', maxWidth: '560px' }}>
          Your next bad review is <span style={{ color: '#00ffa0', textShadow: '0 0 20px rgba(0,255,160,0.5)' }}>preventable</span>
        </h2>
        <p style={{ color: '#547164', fontSize: '15px', margin: '0 auto 32px', maxWidth: '420px' }}>Join 240+ businesses across India that intercept complaints before they go public.</p>
        {user ? (
          <button className="btn-primary-emerald" onClick={onEnterDashboard} style={{ fontSize: '15px', padding: '16px 36px' }}>
            Go to Dashboard →
          </button>
        ) : (
          <button className="btn-primary-emerald" onClick={onAuth} disabled={loading} style={{ fontSize: '15px', padding: '16px 36px' }}>
            {loading ? <Spinner size={16} /> : <Icon.Google />}
            {loading ? 'Activating...' : 'Activate Shield Now →'}
          </button>
        )}
      </div>

      {/* ── FOOTER ── */}
      <footer className="border-t border-surface-border py-12 container-xl z-10 relative">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 mb-10 text-left">
          
          {/* Ecosystem & Brand column */}
          <div className="lg:col-span-2 space-y-5">
            <div className="inline-flex">
              <img src="/shield-logo.webp" alt="Shield.Nuera" className="h-6 w-auto object-contain" />
            </div>
            <p className="text-text-subtle text-sm leading-relaxed max-w-sm">
              Next-generation reputation management and review interception platform engineered for businesses, high reliability, and review security.
            </p>
            
            {/* SaaS Ecosystem */}
            <div>
              <p className="text-text-muted/40 uppercase tracking-widest text-[10px] font-bold mb-2.5">NUERA SAAS ECOSYSTEM</p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { name: 'QR.nuera', href: 'https://qr.nuera.space' },
                  { name: 'Shield.Nuera', href: '#', active: true },
                  { name: 'Cardify', href: 'https://cardify.nuera.space' },
                  { name: 'LMS', href: 'https://lms.nuera.space' },
                  { name: 'cloud', href: 'https://cloud.nuera.space' },
                  { name: 'Studio', href: 'https://studio.nuera.space' },
                  { name: 'Link', href: 'https://link.nuera.space' }
                ].map((saas) => (
                  <a key={saas.name} href={saas.href} 
                     className={`px-3 py-1 rounded-full text-xs border transition-all duration-200 ${
                       saas.active 
                         ? 'bg-brand-500/10 border-brand-500/20 text-brand-400 font-medium' 
                         : 'bg-white/5 border-white/5 text-text-subtle hover:bg-white/10 hover:border-white/10 hover:text-white'
                     }`}>
                    {saas.name}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* PLATFORM Column */}
          <div>
            <p className="text-white font-semibold text-xs tracking-wider uppercase mb-4">Platform</p>
            <div className="space-y-2.5">
              <a href="#product" className="block text-text-subtle text-sm hover:text-white transition-colors" onClick={e => { e.preventDefault(); scrollTo('product'); }}>Features</a>
              <a href="#how-it-works" className="block text-text-subtle text-sm hover:text-white transition-colors" onClick={e => { e.preventDefault(); scrollTo('how-it-works'); }}>Sandbox Demo</a>
              <a href="#feedback" className="block text-text-subtle text-sm hover:text-white transition-colors" onClick={e => { e.preventDefault(); scrollTo('feedback'); }}>Pricing Plans</a>
            </div>
          </div>

          {/* SUPPORT & CONTACT Column */}
          <div>
            <p className="text-white font-semibold text-xs tracking-wider uppercase mb-4">Support & Contact</p>
            <div className="space-y-2.5">
              <a href="mailto:support@nuera.space" className="block text-text-subtle text-sm hover:text-white transition-colors">Support Center</a>
              <a href="mailto:sales@nuera.space" className="block text-text-subtle text-sm hover:text-white transition-colors">Enterprise Sales</a>
              <a href="mailto:hello@nuera.space" className="block text-text-subtle text-sm hover:text-white transition-colors">hello@nuera.space</a>
            </div>
          </div>

          {/* COMPANY & LEGAL Column */}
          <div>
            <p className="text-white font-semibold text-xs tracking-wider uppercase mb-4">Company & Legal</p>
            <div className="space-y-2.5">
              <a href="/privacy" className="block text-text-subtle text-sm hover:text-white transition-colors">Privacy Policy</a>
              <a href="/terms" className="block text-text-subtle text-sm hover:text-white transition-colors">Terms of Service</a>
              
              {/* Status pill */}
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/20 bg-emerald-500/5 text-emerald-400 text-[10px] font-bold tracking-wider mt-3">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                ALL SYSTEMS OPERATIONAL
              </div>
            </div>
          </div>

        </div>
        
        {/* Bottom bar */}
        <div className="border-t border-surface-border pt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-text-subtle text-xs">© 2026 shield.nuera. All rights reserved.</p>
          <p className="text-text-subtle text-xs">
            Engineered by <span className="text-white font-bold tracking-widest text-[11px] ml-1">NU<span className="text-brand-500">Ξ</span>RA</span>
          </p>
        </div>
      </footer>
    </div>
  );
};

const ALL_PLAN_DETAILS = {
  starter: {
    name: 'Starter / Solo',
    color: '#38bdf8',
    features: [
      '1 Private WhatsApp Alert',
      'Basic Analytics',
      'Standard QR Code templates',
      'Email Support (hello@nuera.space)'
    ]
  },
  pro: {
    name: 'Professional / Growth',
    color: '#00ff87',
    features: [
      'Multi-Unit Dashboard',
      'Manager + Owner WhatsApp alerts',
      'Advanced Analytics & Exports',
      'Priority 24/7 Support (hello@nuera.space)',
      'Custom SMS Templates'
    ],
    popular: true
  },
  enterprise: {
    name: 'Enterprise / Power',
    color: '#10b981',
    features: [
      'Dedicated SLA Support (hello@nuera.space)',
      'Custom Domain Branding',
      'Unlimited scans and reports',
      'Full CSV Data Export',
      'White-labeled QR Designs'
    ]
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// UPGRADE MODAL
// ─────────────────────────────────────────────────────────────────────────────
const UpgradeModal = ({ user, businessData, loading, onClose, onPay }) => {
  const currentPlan = user?.subscription?.plan_id?.toLowerCase() || 'starter';
  const businessType = businessData?.businessType || 'gmb';
  const vertical = BUSINESS_TYPES[businessType] || BUSINESS_TYPES.gmb;

  const plans = vertical.plans.map(pId => {
    const details = ALL_PLAN_DETAILS[pId];
    const price = vertical.pricing[pId];
    
    const limitLabel = vertical.multiUnit
      ? `Up to ${vertical.maxUnits[pId]} ${vertical.unitLabel}s`
      : pId === 'starter' ? 'Single Location' : pId === 'pro' ? 'Up to 3 Locations' : 'Unlimited Locations';

    return {
      id: pId,
      name: details.name,
      price,
      color: details.color,
      features: [limitLabel, ...details.features],
      popular: details.popular || false
    };
  });

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(2, 6, 3, 0.85)', backdropFilter: 'blur(16px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', overflowY: 'auto' }}>
      <div style={{ background: '#020603', border: '1px solid rgba(0, 255, 135, 0.15)', borderRadius: '24px', padding: '36px', width: '100%', maxWidth: plans.length === 2 ? '720px' : '1020px', position: 'relative', boxShadow: '0 20px 50px rgba(0, 255, 135, 0.05)', animation: 'fadeInUp 0.3s ease' }}>
        
        <button onClick={onClose} style={{ position: 'absolute', top: '24px', right: '24px', background: 'none', border: 'none', color: '#8fab9a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px', borderRadius: '50%', background: 'rgba(255,255,255,0.02)' }} title="Close">
          <Icon.X />
        </button>

        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', borderRadius: '20px', background: 'rgba(0, 255, 135, 0.08)', border: '1px solid rgba(0, 255, 135, 0.2)', fontSize: '11px', fontWeight: '600', color: '#00ff87', marginBottom: '12px' }}>
            ✦ SUBSCRIPTION PLANS
          </div>
          <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#f0f0ff', margin: 0 }}>Upgrade or Change Your Plan</h3>
          <p style={{ fontSize: '14px', color: '#8fab9a', marginTop: '6px', marginBottom: 0 }}>
            Choose a plan that fits your business needs. You are currently on the <strong style={{ color: '#00ff87', textTransform: 'uppercase' }}>{currentPlan}</strong> plan.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: plans.length === 2 ? '1fr 1fr' : '1fr 1fr 1fr', gap: '20px' }}>
          {plans.map((plan) => {
            const isCurrent = plan.id === currentPlan;
            return (
              <div key={plan.id} style={{ background: plan.popular ? 'rgba(0, 255, 135, 0.03)' : 'rgba(255, 255, 255, 0.02)', border: `1px solid ${isCurrent ? '#00ff87' : plan.popular ? 'rgba(0, 255, 135, 0.3)' : 'rgba(255, 255, 255, 0.06)'}`, borderRadius: '20px', padding: '24px', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                {isCurrent && (
                  <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: '#00ff87', borderRadius: '20px', padding: '2px 12px', fontSize: '10px', fontWeight: '700', color: '#020603', whiteSpace: 'nowrap' }}>
                    CURRENT PLAN
                  </div>
                )}
                {!isCurrent && plan.popular && (
                  <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #00ff87, #059669)', borderRadius: '20px', padding: '2px 12px', fontSize: '10px', fontWeight: '700', color: '#020603', whiteSpace: 'nowrap' }}>
                    POPULAR CHOICE
                  </div>
                )}
                
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: plan.color, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>{plan.name}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                    <span style={{ fontSize: '13px', color: '#8fab9a', fontWeight: '500' }}>₹</span>
                    <span style={{ fontSize: '32px', fontWeight: '900', color: '#f0f0ff', letterSpacing: '-0.02em', lineHeight: 1 }}>{plan.price}</span>
                    <span style={{ fontSize: '12px', color: '#8fab9a' }}>/mo</span>
                  </div>
                </div>
                
                <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)', marginBottom: '16px' }}></div>
                
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px 0', display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1 }}>
                  {plan.features.map((f, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#c0c0d8', lineHeight: 1.3 }}>
                      <span style={{ color: '#00ff87', fontSize: '11px', flexShrink: 0 }}>✓</span> {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => !isCurrent && onPay(plan.id)}
                  disabled={loading || isCurrent}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    background: isCurrent ? 'rgba(255,255,255,0.04)' : plan.popular ? 'linear-gradient(135deg, #00ff87, #059669)' : 'rgba(255,255,255,0.06)',
                    border: isCurrent ? '1px solid rgba(255,255,255,0.08)' : plan.popular ? 'none' : '1px solid rgba(255,255,255,0.08)',
                    color: isCurrent ? '#60607a' : plan.popular ? '#020603' : '#f0f0ff',
                    fontSize: '13px',
                    fontWeight: '700',
                    cursor: isCurrent ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    boxShadow: !isCurrent && plan.popular ? '0 4px 12px rgba(0, 255, 135, 0.25)' : 'none'
                  }}
                >
                  {loading ? <Spinner size={14} /> : null}
                  {isCurrent ? 'Current Plan' : loading ? 'Processing...' : 'Upgrade Plan'}
                  {!loading && !isCurrent && <Icon.ArrowRight />}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PAYMENT PAGE
// ─────────────────────────────────────────────────────────────────────────────
const PaymentPage = ({ user, loading, onPay, onLogout, onGoHome }) => {
  const [selectedVertical, setSelectedVertical] = useState('gmb');
  const [scans, setScans] = useState(500);
  const [avgTicket, setAvgTicket] = useState(1000);
  const [simRating, setSimRating] = useState(0);
  const [simState, setSimState] = useState(''); // 'intercepted' or 'redirected'

  const vertical = BUSINESS_TYPES[selectedVertical] || BUSINESS_TYPES.gmb;

  const plans = vertical.plans.map(pId => {
    const details = ALL_PLAN_DETAILS[pId];
    const price = vertical.pricing[pId];
    
    const limitLabel = vertical.multiUnit
      ? `Up to ${vertical.maxUnits[pId]} ${vertical.unitLabel}s`
      : pId === 'starter' ? 'Single Location' : pId === 'pro' ? 'Up to 3 Locations' : 'Unlimited Locations';

    return {
      id: pId,
      name: details.name,
      price,
      color: details.color,
      features: [limitLabel, ...details.features],
      popular: details.popular || false
    };
  });

  const estimatedLift = Math.round(scans * 0.15 * avgTicket * 0.35);

  const runSimulation = (stars) => {
    setSimRating(stars);
    if (stars <= 3) {
      setSimState('intercepted');
    } else {
      setSimState('redirected');
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#020603', fontFamily: 'Inter, sans-serif', color: '#f0f0ff' }}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeInUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        .plan-card{animation:fadeInUp 0.4s ease forwards; transition:all 0.25s ease}
        .plan-card:hover{transform:translateY(-6px); border-color:rgba(0, 255, 135, 0.4); box-shadow:0 12px 30px rgba(0, 255, 135, 0.05)}
        .vertical-tab{transition:all 0.2s ease; cursor:pointer}
        .vertical-tab:hover{background:rgba(255, 255, 255, 0.04); border-color:rgba(0, 255, 135, 0.3)}
        .slider-thumb::-webkit-slider-thumb{appearance:none; width:16px; height:16px; border-radius:50%; background:#00ff87; cursor:pointer}
      `}</style>
      
      {/* Top nav */}
      <div className="payment-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 32px', borderBottom: '1px solid rgba(0, 255, 135, 0.06)', background: 'rgba(255,255,255,0.01)', backdropFilter: 'blur(12px)' }}>
        <div onClick={onGoHome} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }} title="Go to landing page">
          <LogoWordmark height={32} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '13px', color: '#8fab9a' }}>{user?.email}</span>
          <button onClick={onLogout} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', color: '#8fab9a', fontSize: '13px', cursor: 'pointer' }}>
            <Icon.LogOut /> Sign out
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '50px 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 14px', borderRadius: '20px', background: 'rgba(0, 255, 135, 0.08)', border: '1px solid rgba(0, 255, 135, 0.2)', fontSize: '12px', fontWeight: '600', color: '#00ff87', marginBottom: '20px' }}>
            ✦ PREMIUM REPUTATION PROTECTION
          </div>
          <h1 style={{ fontSize: '42px', fontWeight: '900', color: '#f0f0ff', letterSpacing: '-0.03em', margin: '0 0 12px 0' }}>
            Select your <span style={{ background: 'linear-gradient(135deg, #00ff87, #059669)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>industry vertical</span>
          </h1>
          <p style={{ color: '#8fab9a', fontSize: '16px', margin: 0 }}>Customized plans and feature sets tailored for your business needs.</p>
        </div>

        {/* Industry Vertical Selector */}
        <div className="vertical-tabs-grid">
          {Object.values(BUSINESS_TYPES).map(t => (
            <div
              key={t.id}
              onClick={() => setSelectedVertical(t.id)}
              className="vertical-tab"
              style={{
                background: selectedVertical === t.id ? 'rgba(0, 255, 135, 0.06)' : 'rgba(255, 255, 255, 0.02)',
                border: `1px solid ${selectedVertical === t.id ? '#00ff87' : 'rgba(255, 255, 255, 0.06)'}`,
                borderRadius: '16px',
                padding: '16px',
                textAlign: 'center',
                boxShadow: selectedVertical === t.id ? '0 4px 20px rgba(0, 255, 135, 0.05)' : 'none'
              }}
            >
              <div style={{ color: selectedVertical === t.id ? '#00ff87' : '#8fab9a', display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                {Icon[t.iconName] ? React.createElement(Icon[t.iconName]) : null}
              </div>
              <div style={{ fontSize: '14px', fontWeight: '700', color: selectedVertical === t.id ? '#00ff87' : '#f0f0ff' }}>{t.label.split('(')[0]}</div>
              <div style={{ fontSize: '11px', color: '#8fab9a', marginTop: '4px' }}>Starting at ₹{Object.values(t.pricing)[0]}/mo</div>
            </div>
          ))}
        </div>

        {/* Plans Grid */}
        <div className={`plans-grid ${plans.length === 2 ? 'plans-grid-2' : 'plans-grid-3'}`}>
          {plans.map((plan, idx) => (
            <div key={plan.id} className="plan-card" style={{ animationDelay: `${idx * 0.1}s`, background: plan.popular ? 'rgba(0, 255, 135, 0.03)' : 'rgba(255, 255, 255, 0.02)', border: `1px solid ${plan.popular ? 'rgba(0, 255, 135, 0.3)' : 'rgba(255, 255, 255, 0.06)'}`, borderRadius: '20px', padding: '32px', position: 'relative', display: 'flex', flexDirection: 'column' }}>
              {plan.popular && (
                <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #00ff87, #059669)', borderRadius: '20px', padding: '4px 16px', fontSize: '11px', fontWeight: '700', color: '#020603', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
                  ★ MOST POPULAR
                </div>
              )}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: plan.color, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '8px' }}>{plan.name}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                  <span style={{ fontSize: '14px', color: '#8fab9a', fontWeight: '500' }}>₹</span>
                  <span style={{ fontSize: '44px', fontWeight: '900', color: '#f0f0ff', letterSpacing: '-0.02em', lineHeight: 1 }}>{plan.price}</span>
                  <span style={{ fontSize: '14px', color: '#8fab9a' }}>/mo</span>
                </div>
              </div>
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)', marginBottom: '20px' }}></div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px 0', display: 'flex', flexDirection: 'column', gap: '12px', flexGrow: 1 }}>
                {plan.features.map((f, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#c0c0d8' }}>
                    <span style={{ color: '#00ff87', fontSize: '12px', flexShrink: 0 }}>✓</span> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => onPay(plan.id, selectedVertical)}
                disabled={loading}
                style={{ width: '100%', padding: '14px', borderRadius: '12px', background: plan.popular ? 'linear-gradient(135deg, #00ff87, #059669)' : 'rgba(255,255,255,0.06)', border: plan.popular ? 'none' : '1px solid rgba(255,255,255,0.08)', color: plan.popular ? '#020603' : '#f0f0ff', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: plan.popular ? '0 4px 15px rgba(0, 255, 135, 0.25)' : 'none' }}
              >
                {loading ? <Spinner size={16} /> : null}
                {loading ? 'Processing...' : 'Get Started'}
                {!loading && <Icon.ArrowRight />}
              </button>
            </div>
          ))}
        </div>

        {/* ROI and Simulator Interactive Section */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', background: 'rgba(255, 255, 255, 0.01)', border: '1px solid rgba(0, 255, 135, 0.08)', borderRadius: '24px', padding: '36px' }}>
          
          {/* ROI Calculator */}
          <div>
            <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#f0f0ff', marginBottom: '8px' }}>📈 ROI Calculator</h3>
            <p style={{ color: '#8fab9a', fontSize: '13px', marginBottom: '24px', lineHeight: 1.5 }}>Calculate your business growth and extra revenue generated by filtering reviews.</p>
            
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: '600', color: '#c0c0d8', marginBottom: '8px' }}>
                <span>Estimated Monthly Scans</span>
                <span style={{ color: '#00ff87' }}>{scans} scans</span>
              </div>
              <input
                type="range"
                min="50"
                max="5000"
                step="50"
                value={scans}
                onChange={e => setScans(Number(e.target.value))}
                style={{ width: '100%', height: '6px', background: 'rgba(255, 255, 255, 0.08)', borderRadius: '4px', outline: 'none', appearance: 'none' }}
                className="slider-thumb"
              />
            </div>

            <div style={{ marginBottom: '28px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: '600', color: '#c0c0d8', marginBottom: '8px' }}>
                <span>Average Customer Ticket (INR)</span>
                <span style={{ color: '#00ff87' }}>₹{avgTicket}</span>
              </div>
              <input
                type="range"
                min="100"
                max="5000"
                step="50"
                value={avgTicket}
                onChange={e => setAvgTicket(Number(e.target.value))}
                style={{ width: '100%', height: '6px', background: 'rgba(255, 255, 255, 0.08)', borderRadius: '4px', outline: 'none', appearance: 'none' }}
                className="slider-thumb"
              />
            </div>

            <div style={{ background: 'rgba(0, 255, 135, 0.03)', border: '1px solid rgba(0, 255, 135, 0.1)', borderRadius: '16px', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#8fab9a', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Estimated Monthly Return</div>
                <div style={{ fontSize: '32px', fontWeight: '900', color: '#00ff87', marginTop: '4px' }}>₹{estimatedLift.toLocaleString('en-IN')}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '11px', color: '#8fab9a' }}>Rating Lift (est.)</div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#f0f0ff', marginTop: '2px' }}>+{(0.2 + (scans / 2500)).toFixed(1)} Stars</div>
              </div>
            </div>
          </div>

          {/* Interactive Simulator Widget */}
          <div style={{ borderLeft: '1px solid rgba(255, 255, 255, 0.06)', paddingLeft: '32px' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#f0f0ff', marginBottom: '8px' }}>⚡ Reputation Simulator</h3>
            <p style={{ color: '#8fab9a', fontSize: '13px', marginBottom: '24px', lineHeight: 1.5 }}>Simulate how a customer scan acts on a positive vs negative rating.</p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '14px', color: '#c0c0d8', fontWeight: '600' }}>Rate your experience as a mock customer:</div>
              <div style={{ display: 'flex', gap: '10px' }}>
                {[1, 2, 3, 4, 5].map(stars => (
                  <button
                    key={stars}
                    onClick={() => runSimulation(stars)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '36px', transition: 'transform 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.2)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    {stars <= simRating ? '★' : '☆'}
                  </button>
                ))}
              </div>
            </div>

            {simState === 'intercepted' && (
              <div style={{ animation: 'fadeInUp 0.3s ease', background: 'rgba(255, 84, 112, 0.04)', border: '1px solid rgba(255, 84, 112, 0.2)', borderRadius: '16px', padding: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#ff5470', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>🔴 Intercepted ({simRating} Stars)</span>
                </div>
                <p style={{ fontSize: '12px', color: '#8fab9a', margin: '0 0 12px 0' }}>The negative review is flagged and filtered privately, bypassing Google Maps. Owner gets an immediate WhatsApp alert.</p>
                <div style={{ background: '#075E54', borderRadius: '12px', padding: '10px 14px', maxWidth: '280px', margin: '0 auto', fontSize: '12px', color: '#fff' }}>
                  <strong>WhatsApp Alert:</strong> New private review for {vertical.label.split(' ')[0]}. Rating: {simRating}/5. Reason: Bad experience. Please contact customer!
                </div>
              </div>
            )}

            {simState === 'redirected' && (
              <div style={{ animation: 'fadeInUp 0.3s ease', background: 'rgba(0, 255, 135, 0.04)', border: '1px solid rgba(0, 255, 135, 0.2)', borderRadius: '16px', padding: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#00ff87', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>🟢 Redirected ({simRating} Stars)</span>
                </div>
                <p style={{ fontSize: '12px', color: '#8fab9a', margin: '0 0 12px 0' }}>Happy customer is automatically routed directly to your public review page to boost your average stars.</p>
                <div style={{ background: '#fff', borderRadius: '12px', padding: '10px 14px', maxWidth: '280px', margin: '0 auto', fontSize: '12px', color: '#1a1a1a', border: '1px solid #ccc' }}>
                  <img src="/shield-icon.webp" style={{ height: '16px', float: 'left', marginRight: '6px' }} alt="" />
                  <strong>Redirecting to {vertical.reviewPlatform}...</strong>
                  <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>Loading official review form...</div>
                </div>
              </div>
            )}

            {!simState && (
              <div style={{ textAlign: 'center', padding: '30px', border: '1px dashed rgba(255, 255, 255, 0.1)', borderRadius: '16px', color: '#4e6b5a', fontSize: '13px' }}>
                Click a star rating above to test the system live.
              </div>
            )}
          </div>

        </div>

        <p style={{ textAlign: 'center', color: '#4e6b5a', fontSize: '13px', marginTop: '40px' }}>
          🔒 Secured by Razorpay &nbsp;·&nbsp; Cancel anytime &nbsp;·&nbsp; Support: <a href="mailto:hello@nuera.space" style={{ color: '#00ff87', textDecoration: 'none' }}>hello@nuera.space</a>
        </p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ONBOARDING PAGE
// ─────────────────────────────────────────────────────────────────────────────
const OnboardingPage = ({ user, loading, onSetup, onLogout, onGoHome }) => {
  const plan = getUserPlan(user);
  
  const [form, setForm] = useState({
    businessName: '', businessLocation: '', businessCategory: 'restaurant',
    googleReviewUrl: '', ownerPhone: '', managerPhone: '',
    businessType: 'gmb', customCategory: '', tripadvisorLink: '',
    sorryMessage: ''
  });
  const [units, setUnits] = useState([]);
  const [newUnitLabel, setNewUnitLabel] = useState('');
  const [newUnitLink, setNewUnitLink] = useState('');
  const [step, setStep] = useState(1);
  const totalSteps = 3;

  const handleField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const unitTypeLabel = form.businessType === 'amazon' ? 'Product' : 'Bus';
  const limit = BUSINESS_TYPES[form.businessType]?.maxUnits?.[plan] || 999;

  const addUnit = () => {
    if (!newUnitLabel.trim()) return;
    if (units.length >= limit) {
      alert(`Limit reached: Your current ${plan} subscription allows a maximum of ${limit} ${unitTypeLabel.toLowerCase()}s.`);
      return;
    }
    setUnits(prev => [...prev, { label: newUnitLabel.trim(), reviewLink: newUnitLink.trim() || null }]);
    setNewUnitLabel('');
    setNewUnitLink('');
  };

  const removeUnit = (index) => {
    setUnits(prev => prev.filter((_, i) => i !== index));
  };

  const isFormValid = () => {
    if (!form.businessName.trim() || !form.businessLocation.trim() || !form.ownerPhone.trim()) return false;
    
    if (form.businessType === 'gmb' || form.businessType === 'travels') {
      if (!form.googleReviewUrl.trim()) return false;
    }
    if (form.businessType === 'amazon') {
      if (!form.googleReviewUrl.trim()) return false;
    }
    return true;
  };

  return (
    <div className="onboarding-layout" style={{ background: '#020603', fontFamily: 'Inter, sans-serif', color: '#f0f0ff' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} .field-input{width:100%;background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.08);border-radius:10px;padding:11px 14px;color:#f0f0ff;font-size:14px;font-family:'Inter',sans-serif;outline:none;transition:all 0.2s;box-sizing:border-box} .field-input:focus{border-color:rgba(0,255,135,0.5);background:rgba(0,255,135,0.04);box-shadow:0 0 0 3px rgba(0,255,135,0.1)} .field-input::placeholder{color:#4e6b5a} select.field-input option{background:#041008;color:#f0f0ff}`}</style>
      
      {/* Left panel */}
      <div className="onboarding-left" style={{ background: 'rgba(255,255,255,0.01)', borderRight: '1px solid rgba(0, 255, 135, 0.06)', padding: '40px', display: 'flex', flexDirection: 'column' }}>
        <div onClick={onGoHome} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '48px', cursor: 'pointer' }} title="Go to landing page">
          <LogoWordmark height={34} />
        </div>

        <div style={{ marginBottom: '40px' }}>
          <h2 style={{ fontSize: '26px', fontWeight: '800', color: '#f0f0ff', margin: '0 0 8px 0', letterSpacing: '-0.02em' }}>Setup your<br />reputation shield</h2>
          <p style={{ color: '#8fab9a', fontSize: '14px', lineHeight: 1.6, margin: 0 }}>Configure your custom business vertical in under 2 minutes.</p>
        </div>

        {/* Steps */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {[
            { num: 1, label: 'Business Type', desc: 'Select your industry vertical' },
            { num: 2, label: 'Business Info', desc: 'Name, location & category' },
            { num: 3, label: 'Contact & Links', desc: 'WhatsApp & custom review settings' },
          ].map(s => (
            <div key={s.num} style={{ display: 'flex', alignItems: 'center', gap: '14px', opacity: step === s.num ? 1 : 0.4 }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: step === s.num ? 'linear-gradient(135deg, #00ff87, #059669)' : step > s.num ? 'rgba(0,255,135,0.2)' : 'rgba(255,255,255,0.06)', border: step > s.num ? '1px solid rgba(0,255,135,0.4)' : '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', color: step > s.num ? '#00ff87' : (step === s.num ? '#020603' : '#f0f0ff'), flexShrink: 0 }}>
                {step > s.num ? '✓' : s.num}
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#f0f0ff' }}>{s.label}</div>
                <div style={{ fontSize: '12px', color: '#8fab9a' }}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 'auto' }}>
          <button onClick={onLogout} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#4e6b5a', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0' }}>
            <Icon.LogOut /> Sign out
          </button>
        </div>
      </div>

      {/* Right panel */}
      <div className="onboarding-right" style={{ padding: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflowY: 'auto' }}>
        <div style={{ width: '100%', maxWidth: '500px' }}>
          <div style={{ marginBottom: '32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '12px', color: '#8fab9a', fontWeight: '600', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Step {step} of {totalSteps}</span>
              <span style={{ fontSize: '12px', color: '#00ff87', fontWeight: '600' }}>{Math.round((step / totalSteps) * 100)}% complete</span>
            </div>
            <div style={{ height: '4px', background: 'rgba(255,255,255,0.06)', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(step / totalSteps) * 100}%`, background: 'linear-gradient(90deg, #00ff87, #059669)', borderRadius: '4px', transition: 'width 0.4s ease' }}></div>
            </div>
          </div>

          {step === 1 && (
            <div style={{ animation: 'fadeInUp 0.3s ease' }}>
              <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#f0f0ff', marginBottom: '12px', letterSpacing: '-0.01em' }}>Select Business Type</h3>
              <p style={{ color: '#8fab9a', fontSize: '14px', marginBottom: '24px' }}>Choose the vertical that best fits your business structure.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {Object.values(BUSINESS_TYPES).map(t => {
                  const isAvailable = t.plans.includes(plan);
                  return (
                    <div
                      key={t.id}
                      onClick={() => { if (isAvailable) handleField('businessType', t.id); }}
                      style={{
                        padding: '16px',
                        borderRadius: '12px',
                        background: form.businessType === t.id ? 'rgba(0, 255, 135, 0.08)' : 'rgba(255, 255, 255, 0.02)',
                        border: `1px solid ${form.businessType === t.id ? '#00ff87' : 'rgba(255, 255, 255, 0.08)'}`,
                        cursor: isAvailable ? 'pointer' : 'not-allowed',
                        opacity: isAvailable ? 1 : 0.4,
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                      }}
                    >
                      <span style={{ color: form.businessType === t.id ? '#00ff87' : '#8fab9a', display: 'flex', alignItems: 'center' }}>
                        {Icon[t.iconName] ? React.createElement(Icon[t.iconName]) : null}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#f0f0ff' }}>
                          {t.label} {!isAvailable && <span style={{ color: '#ff5470', fontSize: '11px' }}>(Requires Upgrade)</span>}
                        </div>
                        <div style={{ fontSize: '12px', color: '#8fab9a', marginTop: '2px' }}>{t.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button
                onClick={() => setStep(2)}
                style={{ marginTop: '28px', width: '100%', padding: '14px', borderRadius: '12px', background: 'linear-gradient(135deg, #00ff87, #059669)', border: 'none', color: '#020603', fontSize: '15px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 15px rgba(0,255,135,0.25)' }}
              >
                Continue <Icon.ArrowRight />
              </button>
            </div>
          )}

          {step === 2 && (
            <div style={{ animation: 'fadeInUp 0.3s ease' }}>
              <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#f0f0ff', marginBottom: '24px', letterSpacing: '-0.01em' }}>Business Information</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#9090b0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Business Name *</label>
                  <input className="field-input" type="text" value={form.businessName} onChange={e => handleField('businessName', e.target.value)} placeholder="e.g., Spice Junction Restaurant" required />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#9090b0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Location / Branch *</label>
                  <input className="field-input" type="text" value={form.businessLocation} onChange={e => handleField('businessLocation', e.target.value)} placeholder="e.g., Salem Main Road Branch" required />
                </div>
                 {form.businessType === 'gmb' && (
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#9090b0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Business Category *</label>
                    <select className="field-input" value={form.businessCategory} onChange={e => handleField('businessCategory', e.target.value)}>
                      <option value="restaurant">🍽️ Restaurant</option>
                      <option value="cafe">☕ Café</option>
                      <option value="retail">🛍️ Retail Store</option>
                      <option value="hotel">🏨 Hotel</option>
                      <option value="salon">💇 Salon / Spa</option>
                      <option value="service">🔧 Service Center</option>
                      <option value="healthcare">🏥 Healthcare / Clinic</option>
                      <option value="fitness">🏋️ Gym / Fitness</option>
                      <option value="automotive">🚗 Automotive</option>
                      <option value="education">🎓 Education / Coaching</option>
                      <option value="realestate">🏢 Real Estate</option>
                      <option value="other">✍️ Custom Category...</option>
                    </select>
                  </div>
                )}
                {form.businessCategory === 'other' && form.businessType === 'gmb' && (
                  <div style={{ animation: 'fadeInUp 0.2s ease' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#9090b0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Specify Custom Category Name *</label>
                    <input className="field-input" type="text" value={form.customCategory || ''} onChange={e => handleField('customCategory', e.target.value)} placeholder="e.g., Gym / Bakery / Bookstore" required />
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '28px' }}>
                <button onClick={() => setStep(1)} style={{ flex: 1, padding: '14px', borderRadius: '12px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#8fab9a', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
                  Back
                </button>
                <button
                  onClick={() => {
                    if (form.businessName && form.businessLocation) {
                      setStep(3);
                    } else {
                      alert('Please fill all required fields.');
                    }
                  }}
                  style={{ flex: 2, padding: '14px', borderRadius: '12px', background: 'linear-gradient(135deg, #00ff87, #059669)', border: 'none', color: '#020603', fontSize: '15px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 15px rgba(0,255,135,0.25)' }}
                >
                  Continue <Icon.ArrowRight />
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div style={{ animation: 'fadeInUp 0.3s ease' }}>
              <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#f0f0ff', marginBottom: '24px', letterSpacing: '-0.01em' }}>Contact & Links</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {form.businessType === 'gmb' && (
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#9090b0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Google Review Link *</label>
                    <input className="field-input" type="url" value={form.googleReviewUrl} onChange={e => handleField('googleReviewUrl', e.target.value)} placeholder="https://g.page/r/YOUR_BUSINESS_ID/review" required />
                  </div>
                )}
                {form.businessType === 'travels' && (
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#9090b0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Google Review Link *</label>
                    <input className="field-input" type="url" value={form.googleReviewUrl} onChange={e => handleField('googleReviewUrl', e.target.value)} placeholder="https://g.page/r/YOUR_BUSINESS_ID/review" required />
                  </div>
                )}
                {form.businessType === 'amazon' && (
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#9090b0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Amazon Product Link *</label>
                    <input className="field-input" type="url" value={form.googleReviewUrl} onChange={e => handleField('googleReviewUrl', e.target.value)} placeholder="https://amazon.in/dp/..." required />
                  </div>
                )}

                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#9090b0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Owner WhatsApp Number *</label>
                  <input className="field-input" type="tel" value={form.ownerPhone} onChange={e => handleField('ownerPhone', e.target.value)} placeholder="91XXXXXXXXXX (with country code)" required />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#9090b0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Manager WhatsApp Number <span style={{ color: '#4e6b5a', fontWeight: '400' }}>(optional)</span></label>
                  <input className="field-input" type="tel" value={form.managerPhone} onChange={e => handleField('managerPhone', e.target.value)} placeholder="91XXXXXXXXXX (with country code)" />
                </div>

                {/* Units list builder for Travels/Amazon */}
                {(form.businessType === 'travels' || form.businessType === 'amazon') && (
                  <div style={{ border: '1px solid rgba(0, 255, 135, 0.15)', borderRadius: '12px', padding: '16px', background: 'rgba(0, 255, 135, 0.01)' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#00ff87', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                      Add {unitTypeLabel}s ({units.length} / {limit} added)
                    </label>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                      <input
                        className="field-input"
                        type="text"
                        value={newUnitLabel}
                        onChange={e => setNewUnitLabel(e.target.value)}
                        placeholder={form.businessType === 'amazon' ? 'e.g. iPhone Case (Red)' : 'e.g. Bus No. 42 (TN-01-AB-1234)'}
                        style={{ flex: 1 }}
                      />
                      {form.businessType === 'amazon' && (
                        <input
                          className="field-input"
                          type="url"
                          value={newUnitLink}
                          onChange={e => setNewUnitLink(e.target.value)}
                          placeholder="Amazon Product URL (optional)"
                          style={{ flex: 1 }}
                        />
                      )}
                      <button
                        onClick={addUnit}
                        type="button"
                        style={{ padding: '0 18px', background: 'rgba(0, 255, 135, 0.12)', border: '1px solid rgba(0, 255, 135, 0.3)', borderRadius: '10px', color: '#00ff87', fontWeight: '700', cursor: 'pointer' }}
                      >
                        + Add
                      </button>
                    </div>
                    {units.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '150px', overflowY: 'auto' }}>
                        {units.map((u, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '8px', padding: '8px 12px' }}>
                            <span style={{ fontSize: '13px', color: '#f0f0ff' }}>{u.label}</span>
                            <button
                              onClick={() => removeUnit(i)}
                              type="button"
                              style={{ background: 'none', border: 'none', color: '#ff5470', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '28px' }}>
                <button onClick={() => setStep(2)} style={{ flex: 1, padding: '14px', borderRadius: '12px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#8fab9a', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
                  Back
                </button>
                <button
                  onClick={() => {
                    if (isFormValid()) {
                      onSetup({ ...form, units });
                    } else {
                      alert('Please fill all required fields.');
                    }
                  }}
                  disabled={loading}
                  style={{ flex: 2, padding: '14px', borderRadius: '12px', background: 'linear-gradient(135deg, #00ff87, #059669)', border: 'none', color: '#020603', fontSize: '15px', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 15px rgba(0,255,135,0.25)' }}
                >
                  {loading ? <Spinner size={18} /> : null}
                  {loading ? 'Activating...' : 'Launch Shield'}
                </button>
              </div>
            </div>
          )}
          {/* Support callout at onboarding bottom */}
          <div style={{ marginTop: '32px', textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '16px' }}>
            <span style={{ fontSize: '12px', color: '#60607a' }}>Need help onboarding? Contact <a href="mailto:hello@nuera.space" style={{ color: '#00ff87', textDecoration: 'none' }}>hello@nuera.space</a></span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DASHBOARD PAGE
// ─────────────────────────────────────────────────────────────────────────────
const DashboardPage = ({
  user, businessData, reviewLink, stats, privateReviews, copied, setCopied,
  activeTab, setActiveTab, isEditing, setIsEditing, editForm, setEditForm,
  savingSettings, onSaveSettings, onLogout, loading,
  businessUnits = [], onAddUnit, onDeleteUnit,
  onUpgradeClick,
  onGoHome,
  // Admin props
  adminUsers, adminClients, loadingAdmin, editingUserSub, setEditingUserSub,
  editSubForm, setEditSubForm, onUpdateSubscriptionSubmit, fetchAdminData
}) => {
  const [hoveredStar, setHoveredStar] = useState(null);
  const [adminSearch, setAdminSearch] = useState('');
  const [newUnitLabel, setNewUnitLabel] = useState('');
  const [newUnitLink, setNewUnitLink] = useState('');
  const [addingUnit, setAddingUnit] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (activeTab === 'admin') {
      fetchAdminData();
    }
  }, [activeTab]);

  const userPlan = getUserPlan(user);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <Icon.BarChart /> },
    { id: 'complaints', label: 'Feedback', icon: <Icon.MessageSquare /> },
  ];

  const hasMultiUnit = businessData?.businessType === 'travels' || businessData?.businessType === 'amazon';
  if (hasMultiUnit) {
    tabs.push({ id: 'units', label: businessData?.businessType === 'amazon' ? 'Products' : 'Buses', icon: <Icon.Folder /> });
  }

  // Add AI Analytics & Bulk SMS (Locked/Gated for Starter)
  tabs.push({ id: 'analytics', label: `AI Analytics ${userPlan === 'starter' ? '🔒' : ''}`, icon: <Icon.Zap /> });
  tabs.push({ id: 'sms', label: `Bulk SMS ${userPlan === 'starter' ? '🔒' : ''}`, icon: <Icon.Phone /> });

  // Add White-label & API (Enterprise only, hidden otherwise)
  if (userPlan === 'enterprise' || user?.email === 'hello@nuera.space') {
    tabs.push({ id: 'whitelabel', label: 'White-label & API', icon: <Icon.Shield /> });
  }

  tabs.push({ id: 'settings', label: 'Settings', icon: <Icon.Settings /> });

  if (user?.email === 'hello@nuera.space') {
    tabs.push({ id: 'admin', label: 'Admin Portal', icon: <Icon.Shield /> });
  }

  const statCards = [
    { label: 'Total Scans', value: stats.totalScans, icon: <Icon.TrendingUp />, color: '#00ff87', bg: 'rgba(0, 255, 135, 0.03)', border: 'rgba(0, 255, 135, 0.15)', change: 'All time' },
    { label: '5-Star Redirects', value: stats.redirected5Star, icon: <Icon.Star filled={true} />, color: '#fbbf24', bg: 'rgba(251,191,36,0.04)', border: 'rgba(251,191,36,0.15)', change: 'To Google Maps' },
    { label: 'Private Feedback', value: stats.privateComplaints, icon: <Icon.Bell />, color: '#f87171', bg: 'rgba(248,113,113,0.04)', border: 'rgba(248,113,113,0.15)', change: 'Intercepted' },
    { label: 'WhatsApp Alerts', value: stats.whatsappAlerts, icon: <Icon.Phone />, color: '#34d399', bg: 'rgba(52,211,153,0.04)', border: 'rgba(52,211,153,0.15)', change: 'Sent to team' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#020603', display: 'flex', fontFamily: 'Inter, sans-serif' }}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeInUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        .field-input{width:100%;background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.08);border-radius:10px;padding:11px 14px;color:#f0f0ff;font-size:14px;font-family:'Inter',sans-serif;outline:none;transition:all 0.2s;box-sizing:border-box}
        .field-input:focus{border-color:rgba(0,255,135,0.5);background:rgba(0,255,135,0.04);box-shadow:0 0 0 3px rgba(0,255,135,0.1)}
        .field-input::placeholder{color:#4e6b5a}
        select.field-input option{background:#041008;color:#f0f0ff}
        .table-row:hover td{background:rgba(255,255,255,0.02)!important;color:#e6fff0!important}
        .stat-card:hover{border-color:rgba(0,255,135,0.25)!important;transform:translateY(-2px);transition:all 0.2s ease}
        .nav-link{display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:10px;font-size:14px;font-weight:500;color:#8fab9a;cursor:pointer;transition:all 0.15s ease;text-decoration:none;border:none;background:none;width:100%}
        .nav-link:hover{background:rgba(255,255,255,0.04);color:#f0f0ff}
        .nav-link.active{background:rgba(0,255,135,0.08);color:#00ff87;border:1px solid rgba(0,255,135,0.2)}
      `}</style>

      {/* Mobile hamburger */}
      <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
      </button>
      {/* Sidebar overlay */}
      <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />
      {/* Sidebar */}
      <div className={`dash-sidebar ${sidebarOpen ? 'open' : ''}`}>
        {/* Logo */}
        <div onClick={onGoHome} style={{ display: 'flex', alignItems: 'center', marginBottom: '28px', padding: '0 4px', cursor: 'pointer' }} title="Go to landing page">
          <LogoWordmark height={30} />
        </div>

        {/* Status badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderRadius: '10px', background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.15)', marginBottom: '24px' }}>
          <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#34d399', boxShadow: '0 0 6px #34d399', animation: 'spin 0s', flexShrink: 0 }}></div>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#34d399' }}>Shield Active</div>
            <div style={{ fontSize: '11px', color: '#60607a' }}>{businessData?.businessName}</div>
          </div>
        </div>

        {/* Nav */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }} className={`nav-link ${activeTab === tab.id ? 'active' : ''}` }>
              <span style={{ fontSize: '16px', width: '20px', textAlign: 'center' }}>{tab.icon}</span>
              {tab.label}
              {tab.id === 'complaints' && privateReviews.length > 0 && (
                <span style={{ marginLeft: 'auto', background: 'rgba(248,113,113,0.15)', color: '#f87171', border: '1px solid rgba(248,113,113,0.25)', borderRadius: '10px', padding: '1px 7px', fontSize: '11px', fontWeight: '700' }}>{privateReviews.length}</span>
              )}
            </button>
          ))}
          <a
            href="mailto:hello@nuera.space?subject=Support%20Request%20-%20Shield"
            className="nav-link"
            style={{
              marginTop: 'auto',
              color: '#60607a',
              fontSize: '13px',
              textDecoration: 'none'
            }}
          >
            <span style={{ fontSize: '16px', width: '20px', textAlign: 'center', display: 'inline-flex', alignItems: 'center' }}>
              <Icon.Mail />
            </span>
            hello@nuera.space
          </a>
        </div>

        {/* User info */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '16px', marginTop: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', padding: '0 4px' }}>
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user?.name || 'User'}
                referrerPolicy="no-referrer"
                style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid rgba(0,255,135,0.3)' }}
                onError={e => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #00ff87, #059669)', display: user?.avatar_url ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', color: '#020603', flexShrink: 0 }}>
              {(user?.name || user?.email || '?')[0].toUpperCase()}
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#f0f0ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name || 'User'}</div>
              <div style={{ fontSize: '11px', color: '#4e6b5a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.email}</div>
            </div>
          </div>
          <button onClick={onLogout} style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '9px 12px', borderRadius: '8px', background: 'rgba(248,113,113,0.05)', border: '1px solid rgba(248,113,113,0.1)', color: '#f87171', fontSize: '13px', cursor: 'pointer', transition: 'all 0.15s' }}>
            <Icon.LogOut /> Sign out
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="dash-main">
        
        {/* Header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
          <div>
            <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#f0f0ff', margin: '0 0 4px 0', letterSpacing: '-0.02em' }}>
              {activeTab === 'overview' && 'Dashboard Overview'}
              {activeTab === 'complaints' && 'Customer Feedback'}
              {activeTab === 'units' && (businessData?.businessType === 'amazon' ? 'Products & Links' : 'Fleet & Buses')}
              {activeTab === 'settings' && 'Business Settings'}
              {activeTab === 'admin' && 'Admin Portal'}
            </h1>
            <p style={{ color: '#9090b0', fontSize: '14px', margin: 0 }}>
              {activeTab === 'overview' && 'Your reputation intelligence at a glance'}
              {activeTab === 'complaints' && `${privateReviews.length} private feedback submission${privateReviews.length !== 1 ? 's' : ''} captured`}
              {activeTab === 'units' && (businessData?.businessType === 'amazon' ? 'Manage product lines and view scans' : 'Manage registered vehicles and monitor reviews')}
              {activeTab === 'settings' && 'Manage your business profile configuration'}
              {activeTab === 'admin' && 'Manage registered users and system subscriptions'}
            </p>
          </div>
          {activeTab === 'complaints' && privateReviews.length > 0 && (
            <button
              onClick={() => {
                const plan = getUserPlan(user);
                if (plan === 'starter') {
                  if (window.confirm('🔒 CSV Export is locked on Starter plan. Would you like to upgrade to Pro or Enterprise to download reports?')) {
                    onUpgradeClick();
                  }
                  return;
                }
                const headers = ['Customer Name', 'Phone', 'Rating', 'Feedback', 'Date', 'Unit'];
                const rows = privateReviews.map(r => [
                  r.customer_name || 'Anonymous',
                  r.customer_phone || '',
                  r.rating_given,
                  r.complaint_text || '',
                  new Date(r.timestamp).toLocaleDateString('en-IN'),
                  r.unit_label || ''
                ]);
                const csvContent = "data:text/csv;charset=utf-8," 
                  + [headers.join(','), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))].join('\n');
                
                const encodedUri = encodeURI(csvContent);
                const link = document.createElement("a");
                link.setAttribute("href", encodedUri);
                link.setAttribute("download", `shield_feedback_${Date.now()}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                borderRadius: '10px',
                background: 'rgba(0, 255, 135, 0.08)',
                border: '1px solid rgba(0, 255, 135, 0.2)',
                color: '#00ff87',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              📥 Export CSV {getUserPlan(user) === 'starter' && '🔒'}
            </button>
          )}
        </div>

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'overview' && (
          <div style={{ animation: 'fadeInUp 0.35s ease' }}>
            {/* Review link card */}
            <div style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '24px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: '#00ff87', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '4px' }}>Your Review Collection Link</div>
                  <div style={{ fontSize: '13px', color: '#8fab9a' }}>Share this with customers — WhatsApp, QR code, or table display</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px', borderRadius: '20px', background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)', fontSize: '12px', color: '#34d399', fontWeight: '600' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#34d399', boxShadow: '0 0 5px #34d399' }}></div>
                  LIVE
                </div>
              </div>
              <div className="review-link-row" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div style={{ flex: 1, background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '12px 16px', fontFamily: 'monospace', fontSize: '13px', color: '#a7ffd5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {reviewLink}
                </div>
                <button
                  onClick={() => { navigator.clipboard.writeText(reviewLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  style={{ padding: '12px 20px', borderRadius: '10px', background: copied ? 'rgba(52,211,153,0.12)' : 'rgba(0,255,135,0.12)', border: `1px solid ${copied ? 'rgba(52,211,153,0.3)' : 'rgba(0,255,135,0.3)'}`, color: copied ? '#34d399' : '#00ff87', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s', whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {copied ? <Icon.Check /> : <Icon.Copy />}
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
            </div>

            {/* Stats grid */}
            <div className="dash-stats-grid">
              {statCards.map((card, i) => (
                <div key={i} className="stat-card" style={{ background: card.bg, border: `1px solid ${card.border}`, borderRadius: '14px', padding: '20px', transition: 'all 0.2s ease' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#8fab9a', letterSpacing: '0.04em', textTransform: 'uppercase' }}>{card.label}</div>
                    <div style={{ display: 'inline-flex', alignItems: 'center', color: card.color }}>{card.icon}</div>
                  </div>
                  <div style={{ fontSize: '36px', fontWeight: '900', color: card.color, letterSpacing: '-0.02em', lineHeight: 1, marginBottom: '6px' }}>{card.value}</div>
                  <div style={{ fontSize: '12px', color: '#4e6b5a' }}>{card.change}</div>
                </div>
              ))}
            </div>

            {/* Info cards grid */}
            <div className="dash-info-grid">
              {/* Quick profile */}
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: '#f0f0ff' }}>Business Profile</div>
                  <button onClick={() => setActiveTab('settings')} style={{ fontSize: '12px', color: '#00ff87', background: 'rgba(0,255,135,0.08)', border: '1px solid rgba(0,255,135,0.2)', borderRadius: '8px', padding: '5px 12px', cursor: 'pointer', fontWeight: '600' }}>Edit</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { key: 'Name', val: businessData?.businessName },
                    { key: 'Location', val: businessData?.businessLocation },
                    { key: 'Category', val: businessData?.businessCategory },
                  ].map(r => (
                    <div key={r.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <span style={{ fontSize: '13px', color: '#8fab9a' }}>{r.key}</span>
                      <span style={{ fontSize: '13px', color: '#f0f0ff', fontWeight: '500', textTransform: 'capitalize', textAlign: 'right', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.val || '—'}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent activity */}
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: '#f0f0ff' }}>Recent Feedback</div>
                  {privateReviews.length > 0 && (
                    <button onClick={() => setActiveTab('complaints')} style={{ fontSize: '12px', color: '#00ff87', background: 'rgba(0,255,135,0.08)', border: '1px solid rgba(0,255,135,0.2)', borderRadius: '8px', padding: '5px 12px', cursor: 'pointer', fontWeight: '600' }}>View all</button>
                  )}
                </div>
                {privateReviews.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 0' }}>
                    <div style={{ color: '#00ff87', display: 'flex', justifyContent: 'center', marginBottom: '8px' }}><Icon.CheckCircle /></div>
                    <div style={{ fontSize: '14px', color: '#8fab9a' }}>No feedback yet!</div>
                    <div style={{ fontSize: '12px', color: '#4e6b5a', marginTop: '4px' }}>Your customers are loving you</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {privateReviews.slice(0, 3).map(r => (
                      <div key={r.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px', borderRadius: '10px', background: 'rgba(248,113,113,0.04)', border: '1px solid rgba(248,113,113,0.1)' }}>
                        <div style={{ background: 'rgba(248,113,113,0.12)', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f87171', fontSize: '13px', fontWeight: '700', flexShrink: 0 }}>
                          {r.rating_given}★
                        </div>
                        <div style={{ overflow: 'hidden' }}>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#f0f0ff', marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.customer_name}</div>
                          <div style={{ fontSize: '12px', color: '#8fab9a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.complaint_text || 'No comment'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Scan Activity Chart Card */}
            <div style={{ marginTop: '24px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '24px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: '#f0f0ff' }}>Scan & Redirect Trends</div>
                  <div style={{ fontSize: '12px', color: '#8fab9a', marginTop: '2px' }}>Hourly tracking of QR scans vs successful redirects</div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['7D', '30D', 'ALL'].map(t => (
                    <button key={t} style={{ padding: '4px 10px', borderRadius: '6px', background: t === '7D' ? 'rgba(0, 255, 135, 0.08)' : 'rgba(255,255,255,0.03)', border: `1px solid ${t === '7D' ? 'rgba(0, 255, 135, 0.2)' : 'rgba(255, 255, 255, 0.05)'}`, fontSize: '11px', color: t === '7D' ? '#00ff87' : '#8fab9a', fontWeight: '600' }}>{t}</button>
                  ))}
                </div>
              </div>

              {/* Mock Chart Visualization */}
              <div style={{ height: '140px', display: 'flex', alignItems: 'flex-end', gap: '8px', paddingBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.05)', filter: userPlan === 'starter' ? 'blur(4px)' : 'none', opacity: userPlan === 'starter' ? 0.3 : 1 }}>
                {[30, 45, 35, 60, 40, 75, 50, 90, 65, 80, 55, 110].map((val, idx) => (
                  <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '100%', height: `${val}px`, background: 'linear-gradient(to top, rgba(0, 255, 135, 0.02), rgba(0, 255, 135, 0.4))', borderTop: '2px solid #00ff87', borderRadius: '4px 4px 0 0' }}></div>
                    <span style={{ fontSize: '10px', color: '#4e6b5a' }}>{idx + 1} PM</span>
                  </div>
                ))}
              </div>

              {/* Blurred Overlay for Starter Tier */}
              {userPlan === 'starter' && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(2, 6, 3, 0.5)', backdropFilter: 'blur(3px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', textAlign: 'center' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '4px 12px', fontSize: '11px', fontWeight: '600', color: '#ff5470', marginBottom: '8px' }}>
                    🔒 PRO FEATURE
                  </div>
                  <h4 style={{ fontSize: '16px', fontWeight: '700', color: '#f0f0ff', margin: '0 0 6px 0' }}>Hourly Trend Charts Locked</h4>
                  <p style={{ fontSize: '12px', color: '#8fab9a', margin: '0 0 16px 0', maxWidth: '340px' }}>Upgrade to a Professional or Enterprise plan to view detailed time-based scan traffic analytics.</p>
                  <button onClick={onUpgradeClick} style={{ padding: '8px 20px', borderRadius: '8px', background: 'linear-gradient(135deg, #00ff87, #059669)', border: 'none', color: '#020603', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>Upgrade Plan</button>
                </div>
              )}
            </div>

          </div>
        )}

        {/* ── COMPLAINTS TAB ── */}
        {activeTab === 'complaints' && (
          <div style={{ animation: 'fadeInUp 0.35s ease' }}>
            {privateReviews.length === 0 ? (
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '60px', textAlign: 'center' }}>
                <div style={{ color: '#00ff87', display: 'flex', justifyContent: 'center', marginBottom: '12px' }}><Icon.Shield /></div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#f0f0ff', marginBottom: '6px' }}>No feedback intercepted yet</div>
                <div style={{ fontSize: '14px', color: '#8fab9a' }}>When customers leave 1-4 star ratings, they'll appear here privately instead of on Google.</div>
              </div>
            ) : (
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 80px 2fr 1fr 1.2fr', padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.01)' }}>
                  {['Customer', 'Phone', 'Rating', 'Feedback', hasMultiUnit ? (businessData?.businessType === 'amazon' ? 'Product' : 'Bus') : 'Branch', 'Date'].map(h => (
                    <div key={h} style={{ fontSize: '11px', fontWeight: '700', color: '#60607a', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</div>
                  ))}
                </div>
                {privateReviews.map((r, i) => (
                  <div key={r.id} className="table-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 80px 2fr 1fr 1.2fr', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.03)', transition: 'background 0.15s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'rgba(99,102,241,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', color: '#818cf8', flexShrink: 0 }}>
                        {(r.customer_name || 'A')[0].toUpperCase()}
                      </div>
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#f0f0ff' }}>{r.customer_name || 'Anonymous'}</span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#9090b0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ color: '#60607a', fontSize: '12px' }}><Icon.Phone /></span>
                      {r.customer_phone || '—'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '3px 9px', borderRadius: '20px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)', fontSize: '12px', fontWeight: '700', color: '#f87171' }}>
                        {r.rating_given}★
                      </div>
                    </div>
                    <div style={{ fontSize: '13px', color: '#9090b0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '16px', display: 'flex', alignItems: 'center' }}>
                      {r.complaint_text || <span style={{ color: '#60607a', fontStyle: 'italic' }}>No comment</span>}
                    </div>
                    <div style={{ fontSize: '13px', color: '#9090b0', display: 'flex', alignItems: 'center' }}>
                      {r.unit_label || 'Main'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#60607a', display: 'flex', alignItems: 'center' }}>
                      {new Date(r.timestamp).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── UNITS TAB ── */}
        {activeTab === 'units' && (
          <div style={{ animation: 'fadeInUp 0.35s ease' }}>
            <div style={{ display: 'flex', gap: '24px', alignItems: 'start' }}>
              
              {/* Unit list */}
              <div style={{ flex: 2, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#f0f0ff' }}>
                    Configured {businessData?.businessType === 'amazon' ? 'Products' : 'Buses'} ({businessUnits.length})
                  </h3>
                </div>
                {businessUnits.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', border: '1px dashed rgba(255, 255, 255, 0.08)', borderRadius: '12px' }}>
                    <div style={{ fontSize: '14px', color: '#8fab9a' }}>No units added yet.</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {businessUnits.map(unit => (
                      <div key={unit.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: '12px', padding: '16px' }}>
                        <div>
                          <div style={{ fontSize: '15px', fontWeight: '600', color: '#f0f0ff' }}>{unit.unit_label}</div>
                          <div style={{ fontSize: '12px', color: '#8fab9a', marginTop: '4px', display: 'flex', gap: '16px' }}>
                            <span>Scans: <strong>{unit.scan_count || 0}</strong></span>
                            <span>Complaints: <strong>{unit.complaint_count || 0}</strong></span>
                          </div>
                          {unit.review_link && (
                            <div style={{ fontSize: '11px', color: '#00ff87', fontFamily: 'monospace', marginTop: '6px' }}>{unit.review_link}</div>
                          )}
                        </div>
                        
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(unit.qr_code_url);
                              alert('QR Link copied to clipboard!');
                            }}
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#00ff87', background: 'rgba(0,255,135,0.08)', border: '1px solid rgba(0,255,135,0.2)', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', fontWeight: '600' }}
                          >
                            Copy Link
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to delete ${unit.unit_label}?`)) {
                                onDeleteUnit(unit.id);
                              }
                            }}
                            style={{ padding: '6px 12px', borderRadius: '8px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', color: '#f87171', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add unit form */}
              <div style={{ flex: 1, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '24px' }}>
                {(() => {
                  const plan = getUserPlan(user);
                  const maxAllowed = BUSINESS_TYPES[businessData?.businessType]?.maxUnits?.[plan] || 999;
                  const limitReached = businessUnits.length >= maxAllowed;
                  const isStarter = plan === 'starter';
                  
                  return (
                    <>
                      <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#f0f0ff', marginBottom: '6px' }}>
                        Add New {businessData?.businessType === 'amazon' ? 'Product' : 'Bus'}
                      </h3>
                      <div style={{ fontSize: '12px', color: '#8fab9a', marginBottom: '20px' }}>
                        Plan limit: {businessUnits.length} / {maxAllowed} allowed
                      </div>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#9090b0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                            Label Name *
                          </label>
                          <input
                            className="field-input"
                            type="text"
                            value={newUnitLabel}
                            onChange={e => setNewUnitLabel(e.target.value)}
                            placeholder={businessData?.businessType === 'amazon' ? 'e.g. Red Water Bottle' : 'e.g. Bus 42 (KA-01-A-1234)'}
                            disabled={limitReached}
                          />
                        </div>
                        
                        <div>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#9090b0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                            Custom Review URL {isStarter && '🔒'}
                          </label>
                          <input
                            className="field-input"
                            type="url"
                            value={isStarter ? '' : newUnitLink}
                            onChange={e => setNewUnitLink(e.target.value)}
                            placeholder={isStarter ? 'Upgrade to Pro/Enterprise to customize' : 'Optional destination review URL'}
                            disabled={limitReached || isStarter}
                          />
                          {isStarter && (
                            <div style={{ fontSize: '11px', color: '#ff5470', marginTop: '5px' }}>
                              🔒 Starter tier units inherit the main URL. Upgrade to customize redirect URLs per unit.
                            </div>
                          )}
                        </div>

                        <button
                          onClick={async () => {
                            if (limitReached) return;
                            if (!newUnitLabel.trim()) {
                              alert('Label name is required');
                              return;
                            }
                            setAddingUnit(true);
                            const res = await onAddUnit(newUnitLabel, isStarter ? null : newUnitLink);
                            setAddingUnit(false);
                            if (res.success) {
                              setNewUnitLabel('');
                              setNewUnitLink('');
                            } else {
                              alert(res.error);
                            }
                          }}
                          disabled={limitReached || addingUnit}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '10px',
                            background: limitReached ? 'rgba(255,255,255,0.04)' : 'linear-gradient(135deg, #00ff87, #059669)',
                            border: 'none',
                            color: limitReached ? '#4e6b5a' : '#020603',
                            fontSize: '14px',
                            fontWeight: '700',
                            cursor: limitReached || addingUnit ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          {addingUnit ? 'Adding...' : limitReached ? '🔒 Limit Reached' : `+ Add ${businessData?.businessType === 'amazon' ? 'Product' : 'Bus'}`}
                        </button>

                        {limitReached && (
                          <div style={{ fontSize: '12px', color: '#ff5470', textAlign: 'center', marginTop: '8px', lineHeight: 1.4 }}>
                            You have reached the maximum allowed units for your plan. Please <span style={{ textDecoration: 'underline', cursor: 'pointer', color: '#00ff87', fontWeight: 'bold' }} onClick={onUpgradeClick}>upgrade</span> to add more.
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>

            </div>
          </div>
        )}

        {/* ── AI ANALYTICS TAB ── */}
        {activeTab === 'analytics' && (
          <div style={{ animation: 'fadeInUp 0.35s ease' }}>
            {userPlan === 'starter' ? (
              <div style={{ position: 'relative', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '20px', padding: '60px 40px', textAlign: 'center', overflow: 'hidden' }}>
                <div style={{ filter: 'blur(8px)', opacity: 0.15, pointerEvents: 'none', marginBottom: '20px' }}>
                  <div style={{ height: '20px', width: '200px', background: '#fff', marginBottom: '10px' }}></div>
                  <div style={{ height: '150px', background: '#fff', borderRadius: '10px' }}></div>
                </div>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(2, 6, 3, 0.7)', backdropFilter: 'blur(4px)', padding: '24px' }}>
                  <span style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</span>
                  <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#f0f0ff', marginBottom: '8px' }}>AI Sentiment Analytics</h3>
                  <p style={{ color: '#8fab9a', fontSize: '14px', maxWidth: '400px', margin: '0 0 24px 0', lineHeight: 1.5 }}>
                    Unlock natural language processing and customer feedback trends. Requires a <strong>Professional</strong> or <strong>Enterprise</strong> plan.
                  </p>
                  <button onClick={onUpgradeClick} style={{ padding: '12px 28px', borderRadius: '12px', background: 'linear-gradient(135deg, #00ff87, #059669)', border: 'none', color: '#020603', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 15px rgba(0,255,135,0.25)' }}>
                    Upgrade Now
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '24px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#f0f0ff', marginBottom: '12px' }}>AI Sentiment Analytics</h3>
                <p style={{ color: '#8fab9a', fontSize: '14px', marginBottom: '24px' }}>Analyze positive, negative, and neutral keywords from customer scans.</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ padding: '16px', borderRadius: '12px', background: 'rgba(52,211,153,0.04)', border: '1px solid rgba(52,211,153,0.1)' }}>
                    <div style={{ fontSize: '12px', color: '#8fab9a' }}>Positive Sentiments</div>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: '#34d399', marginTop: '6px' }}>84%</div>
                  </div>
                  <div style={{ padding: '16px', borderRadius: '12px', background: 'rgba(251,191,36,0.04)', border: '1px solid rgba(251,191,36,0.1)' }}>
                    <div style={{ fontSize: '12px', color: '#8fab9a' }}>Neutral Sentiments</div>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: '#fbbf24', marginTop: '6px' }}>11%</div>
                  </div>
                  <div style={{ padding: '16px', borderRadius: '12px', background: 'rgba(248,113,113,0.04)', border: '1px solid rgba(248,113,113,0.1)' }}>
                    <div style={{ fontSize: '12px', color: '#8fab9a' }}>Negative Sentiments</div>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: '#f87171', marginTop: '6px' }}>5%</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── BULK SMS TAB ── */}
        {activeTab === 'sms' && (
          <div style={{ animation: 'fadeInUp 0.35s ease' }}>
            {userPlan === 'starter' ? (
              <div style={{ position: 'relative', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '20px', padding: '60px 40px', textAlign: 'center', overflow: 'hidden' }}>
                <div style={{ filter: 'blur(8px)', opacity: 0.15, pointerEvents: 'none', marginBottom: '20px' }}>
                  <div style={{ height: '20px', width: '200px', background: '#fff', marginBottom: '10px' }}></div>
                  <div style={{ height: '150px', background: '#fff', borderRadius: '10px' }}></div>
                </div>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(2, 6, 3, 0.7)', backdropFilter: 'blur(4px)', padding: '24px' }}>
                  <span style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</span>
                  <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#f0f0ff', marginBottom: '8px' }}>Bulk SMS Review Invites</h3>
                  <p style={{ color: '#8fab9a', fontSize: '14px', maxWidth: '400px', margin: '0 0 24px 0', lineHeight: 1.5 }}>
                    Send automated SMS review invitations to your customer list. Requires a <strong>Professional</strong> or <strong>Enterprise</strong> plan.
                  </p>
                  <button onClick={onUpgradeClick} style={{ padding: '12px 28px', borderRadius: '12px', background: 'linear-gradient(135deg, #00ff87, #059669)', border: 'none', color: '#020603', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 15px rgba(0,255,135,0.25)' }}>
                    Upgrade Now
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '24px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#f0f0ff', marginBottom: '12px' }}>Bulk SMS Review Campaigns</h3>
                <p style={{ color: '#8fab9a', fontSize: '14px', marginBottom: '24px' }}>Import customer phone numbers and send bulk SMS invitations to leave a review.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '400px' }}>
                  <textarea className="field-input" rows={4} placeholder="Paste phone numbers (one per line)..." />
                  <button style={{ padding: '12px', borderRadius: '10px', background: 'linear-gradient(135deg, #00ff87, #059669)', border: 'none', color: '#020603', fontWeight: '700', cursor: 'pointer' }}>
                    Send Invites
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── WHITE-LABEL & API TAB ── */}
        {activeTab === 'whitelabel' && (
          <div style={{ animation: 'fadeInUp 0.35s ease' }}>
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '24px', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#f0f0ff', marginBottom: '12px' }}>Custom Domain Branding</h3>
              <p style={{ color: '#8fab9a', fontSize: '14px', marginBottom: '16px' }}>Route your QR review pages through your own domain name (e.g. feedback.yourcompany.com).</p>
              <input className="field-input" type="text" placeholder="feedback.mybrand.com" style={{ maxWidth: '400px', marginBottom: '12px' }} />
              <button style={{ padding: '10px 20px', borderRadius: '8px', background: 'rgba(0, 255, 135, 0.1)', border: '1px solid rgba(0, 255, 135, 0.3)', color: '#00ff87', fontWeight: '600', cursor: 'pointer' }}>Configure CNAME</button>
            </div>
            
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#f0f0ff', marginBottom: '12px' }}>Developer API Key</h3>
              <p style={{ color: '#8fab9a', fontSize: '14px', marginBottom: '16px' }}>Access review metrics and trigger alerts programmatically.</p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <input className="field-input" type="password" value="shield_live_ak902381023812903" readOnly style={{ maxWidth: '400px', fontFamily: 'monospace' }} />
                <button onClick={() => alert('API key copied')} style={{ padding: '10px 20px', borderRadius: '8px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', color: '#f0f0ff', fontWeight: '600', cursor: 'pointer' }}>Copy Key</button>
              </div>
            </div>
          </div>
        )}

        {/* ── SETTINGS TAB ── */}
        {activeTab === 'settings' && (
          <div style={{ animation: 'fadeInUp 0.35s ease', maxWidth: '680px' }}>
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#f0f0ff' }}>Business Profile</div>
                  <div style={{ fontSize: '13px', color: '#9090b0', marginTop: '2px' }}>Manage your contact details and links</div>
                </div>
                {!isEditing ? (
                  <button onClick={() => setIsEditing(true)} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', borderRadius: '9px', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', color: '#818cf8', fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s' }}>
                    <Icon.Edit /> Edit
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => setIsEditing(false)} style={{ padding: '9px 16px', borderRadius: '9px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#9090b0', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                      Cancel
                    </button>
                    <button onClick={onSaveSettings} disabled={savingSettings} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '9px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', color: 'white', fontSize: '13px', fontWeight: '600', cursor: savingSettings ? 'not-allowed' : 'pointer', opacity: savingSettings ? 0.7 : 1 }}>
                      {savingSettings ? <Spinner size={14} /> : <Icon.Save />}
                      {savingSettings ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>

              {/* Fields */}
              <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {!isEditing ? (
                  // View mode
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                    {(() => {
                      const plan = user?.subscription?.plan_id?.toLowerCase() || 'pro';
                      const hasSorryAccess = plan !== 'starter';
                      const hasManagerAccess = plan !== 'starter';
                      return [
                        { label: 'Business Name', val: businessData?.businessName || editForm.businessName },
                        { label: 'Location / Branch', val: businessData?.businessLocation || editForm.businessLocation },
                        { label: 'Category', val: (businessData?.businessCategory === 'other' ? businessData?.customCategory : (businessData?.customCategory || businessData?.businessCategory)) || editForm.businessCategory },
                        { label: 'Google Review URL', val: businessData?.googleReviewUrl || editForm.googleReviewUrl, mono: true },
                        { label: 'Owner WhatsApp', val: businessData?.ownerPhone || editForm.ownerPhone },
                        { label: 'Manager WhatsApp', val: hasManagerAccess ? (businessData?.managerPhone || editForm.managerPhone || 'Not configured') : '🔒 Upgrade to Pro/Enterprise to add manager' },
                        { label: 'Apology Message', val: hasSorryAccess ? (businessData?.sorryMessage || editForm.sorryMessage || 'Default ("We\'d love to make it right")') : '🔒 Upgrade to Pro/Enterprise to customize' }
                      ].map((row, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <span style={{ fontSize: '13px', color: '#9090b0', fontWeight: '500', minWidth: '160px' }}>{row.label}</span>
                          <span style={{ fontSize: '13px', color: row.val === 'Not configured' || row.val.startsWith('🔒') ? '#60607a' : '#f0f0ff', fontWeight: '500', textAlign: 'right', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: row.mono ? 'monospace' : 'inherit', textTransform: row.mono ? 'none' : 'capitalize' }}>
                            {row.val || '—'}
                          </span>
                        </div>
                      ));
                    })()}
                  </div>
                ) : (
                  // Edit mode
                  <div style={{ display: 'grid', gap: '18px', gridTemplateColumns: '1fr 1fr' }}>
                    {(() => {
                      const plan = user?.subscription?.plan_id?.toLowerCase() || 'pro';
                      const hasSorryAccess = plan !== 'starter';

                      const fields = [
                        { key: 'businessName', label: 'Business Name', type: 'text', placeholder: 'Spice Junction Restaurant', full: true },
                        { key: 'businessLocation', label: 'Location / Branch', type: 'text', placeholder: 'Salem Main Road' },
                        { key: 'businessCategory', label: 'Category', type: 'select', options: [
                          { val: 'restaurant', label: '🍽️ Restaurant' },
                          { val: 'cafe', label: '☕ Café' },
                          { val: 'retail', label: '🛍️ Retail Store' },
                          { val: 'hotel', label: '🏨 Hotel' },
                          { val: 'salon', label: '💇 Salon / Spa' },
                          { val: 'service', label: '🔧 Service Center' },
                          { val: 'healthcare', label: '🏥 Healthcare' },
                          { val: 'fitness', label: '🏋️ Gym / Fitness' },
                          { val: 'automotive', label: '🚗 Automotive' },
                          { val: 'education', label: '🎓 Education' },
                          { val: 'realestate', label: '🏢 Real Estate' },
                          { val: 'other', label: '✍️ Custom Category...' }
                        ] }
                      ];

                      if (editForm.businessCategory === 'other') {
                        fields.push({ key: 'customCategory', label: 'Specify Custom Category Name', type: 'text', placeholder: 'e.g. Gym / Bakery / Boutique', full: false });
                      }

                      const hasManagerAccess = plan !== 'starter';

                      fields.push(
                        { key: 'googleReviewUrl', label: 'Google Review URL', type: 'url', placeholder: 'https://g.page/r/...', full: true },
                        { key: 'ownerPhone', label: 'Owner WhatsApp', type: 'tel', placeholder: '91XXXXXXXXXX' },
                        { key: 'managerPhone', label: 'Manager WhatsApp (optional)', type: 'tel', placeholder: '91XXXXXXXXXX', disabled: !hasManagerAccess, help: !hasManagerAccess ? '🔒 Upgrade to Professional/Enterprise to add manager notifications' : 'Manager will also receive instant WhatsApp review alerts.' },
                        { key: 'sorryMessage', label: 'Custom Apology Message', type: 'textarea', placeholder: 'e.g., We\'d love to make it right. Tell us what went wrong so we can improve...', full: true, disabled: !hasSorryAccess, help: !hasSorryAccess ? '🔒 Upgrade to Professional/Enterprise to customize your apology message' : 'Shown to customers who rate 1-4 stars before collecting private feedback.' }
                      );

                      return fields.map(f => (
                        <div key={f.key} style={{ gridColumn: f.full ? '1 / -1' : 'auto' }}>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#9090b0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{f.label}</label>
                          {f.type === 'select' ? (
                            <select className="field-input" value={editForm[f.key]} onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}>
                              {f.options.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
                            </select>
                          ) : f.type === 'textarea' ? (
                            <textarea className="field-input" rows={3} value={editForm[f.key]} onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} disabled={f.disabled} style={{ resize: 'vertical', minHeight: '80px', fontFamily: 'inherit' }} />
                          ) : (
                            <input className="field-input" type={f.type} value={editForm[f.key]} onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} />
                          )}
                          {f.help && <div style={{ fontSize: '11px', color: f.disabled ? '#ff5470' : '#8fab9a', marginTop: '5px' }}>{f.help}</div>}
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>
            </div>

            {/* Subscription details */}
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', padding: '24px', marginTop: '16px' }}>
              <div style={{ fontSize: '15px', fontWeight: '700', color: '#f0f0ff', marginBottom: '16px' }}>Subscription Details</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ fontSize: '13px', color: '#9090b0' }}>Product Name</span>
                  <span style={{ fontSize: '13px', color: '#f0f0ff', fontWeight: '600' }}>Shield.Nuera Reputation Protection</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ fontSize: '13px', color: '#9090b0' }}>Plan Tier</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '13px', color: '#818cf8', fontWeight: '700', textTransform: 'uppercase' }}>
                      {user?.subscription?.plan_id || 'Premium'}
                    </span>
                    <button
                      onClick={onUpgradeClick}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #00ff87, #059669)',
                        border: 'none',
                        color: '#020603',
                        fontSize: '11px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(0, 255, 135, 0.2)'
                      }}
                    >
                      Change Plan
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ fontSize: '13px', color: '#9090b0' }}>Subscription Status</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '20px', background: 'rgba(52,211,153,0.1)', color: '#34d399', fontSize: '12px', fontWeight: '600', border: '1px solid rgba(52,211,153,0.2)' }}>
                    Active
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
                  <span style={{ fontSize: '13px', color: '#9090b0' }}>Expiration Date</span>
                  <span style={{ fontSize: '13px', color: '#fbbf24', fontWeight: '600' }}>
                    {user?.subscription?.current_period_end 
                      ? new Date(user.subscription.current_period_end * 1000).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                      : 'Lifetime Free / Custom Plan'}
                  </span>
                </div>
              </div>
            </div>

            {/* Danger zone */}
            <div style={{ background: 'rgba(248,113,113,0.04)', border: '1px solid rgba(248,113,113,0.12)', borderRadius: '16px', padding: '20px 24px', marginTop: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#f87171', marginBottom: '6px' }}>Danger Zone</div>
              <div style={{ fontSize: '13px', color: '#9090b0', marginBottom: '14px' }}>Signing out will end your session. Your data is safely preserved.</div>
              <button onClick={onLogout} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 18px', borderRadius: '9px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', color: '#f87171', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                <Icon.LogOut /> Sign out of Shield.Nuera
              </button>
            </div>
          </div>
        )}

        {/* ── ADMIN PORTAL TAB ── */}
        {activeTab === 'admin' && (
          <div style={{ animation: 'fadeInUp 0.35s ease' }}>
            {/* Stats summary */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '14px', padding: '20px' }}>
                <div style={{ fontSize: '12px', color: '#9090b0', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.04em', marginBottom: '8px' }}>Total Registered Users</div>
                <div style={{ fontSize: '32px', fontWeight: '900', color: '#f0f0ff' }}>{adminUsers.length}</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '14px', padding: '20px' }}>
                <div style={{ fontSize: '12px', color: '#9090b0', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.04em', marginBottom: '8px' }}>Active Subscriptions</div>
                <div style={{ fontSize: '32px', fontWeight: '900', color: '#34d399' }}>
                  {adminUsers.filter(u => u.subscription?.status === 'active').length}
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '14px', padding: '20px' }}>
                <div style={{ fontSize: '12px', color: '#9090b0', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.04em', marginBottom: '8px' }}>Configured Businesses</div>
                <div style={{ fontSize: '32px', fontWeight: '900', color: '#00ff87' }}>{adminClients.length}</div>
              </div>
            </div>

            {/* Filter / Search */}
            <div style={{ marginBottom: '20px' }}>
              <input
                className="field-input"
                type="text"
                placeholder="Search users by name, email, or business profile..."
                value={adminSearch}
                onChange={e => setAdminSearch(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>

            {/* User List Table */}
            {loadingAdmin ? (
              <div style={{ textAlign: 'center', padding: '48px 0' }}>
                <Spinner size={32} />
                <div style={{ fontSize: '14px', color: '#8fab9a', marginTop: '12px' }}>Loading platform directories...</div>
              </div>
            ) : (
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '16px', overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.8fr 1.2fr 1.2fr 100px', padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.01)' }}>
                  {['User Account', 'Business Profile', 'Stats (Scans/Feedback)', 'Plan & Expiration', 'Action'].map(h => (
                    <div key={h} style={{ fontSize: '11px', fontWeight: '700', color: '#4e6b5a', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</div>
                  ))}
                </div>

                {adminUsers
                  .filter(u => {
                    const client = adminClients.find(c => c.user_id === u.email);
                    const sTerm = adminSearch.toLowerCase();
                    return (
                      u.name?.toLowerCase().includes(sTerm) ||
                      u.email?.toLowerCase().includes(sTerm) ||
                      client?.business_name?.toLowerCase().includes(sTerm)
                    );
                  })
                  .map(u => {
                    const client = adminClients.find(c => c.user_id === u.email);
                    return (
                      <div key={u.id} className="table-row" style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.8fr 1.2fr 1.2fr 100px', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.03)', alignItems: 'center' }}>
                        {/* User Account */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(0, 255, 135, 0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', color: '#00ff87', flexShrink: 0 }}>
                            {u.avatar_url ? <img src={u.avatar_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%' }} /> : (u.name || 'U')[0].toUpperCase()}
                          </div>
                          <div style={{ overflow: 'hidden' }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#f0f0ff', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{u.name || 'User'}</div>
                            <div style={{ fontSize: '11px', color: '#4e6b5a', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{u.email}</div>
                          </div>
                        </div>

                        {/* Business Profile */}
                        <div>
                          {client ? (
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: '#f0f0ff' }}>{client.business_name}</div>
                              <div style={{ fontSize: '11px', color: '#8fab9a', textTransform: 'capitalize' }}>{client.category} · {client.location_branch}</div>
                            </div>
                          ) : (
                            <span style={{ fontSize: '12px', color: '#4e6b5a', fontStyle: 'italic' }}>Pending Setup</span>
                          )}
                        </div>

                        {/* Stats */}
                        <div>
                          {client ? (
                            <div style={{ fontSize: '13px', color: '#8fab9a' }}>
                              <strong style={{ color: '#f0f0ff' }}>{client.total_scans}</strong> scans &nbsp;·&nbsp; <strong style={{ color: '#f87171' }}>{client.filtered_complaints_count}</strong> feedback
                            </div>
                          ) : (
                            <span style={{ fontSize: '12px', color: '#4e6b5a' }}>—</span>
                          )}
                        </div>

                        {/* Plan & Expiration */}
                        <div>
                          {u.subscription ? (
                            <div>
                              <span style={{ display: 'inline-flex', padding: '1px 7px', borderRadius: '12px', background: u.subscription.status === 'active' ? 'rgba(52,211,153,0.1)' : 'rgba(248,113,113,0.1)', color: u.subscription.status === 'active' ? '#34d399' : '#f87171', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px', border: u.subscription.status === 'active' ? '1px solid rgba(52,211,153,0.2)' : '1px solid rgba(248,113,113,0.2)' }}>
                                {u.subscription.plan_id}
                              </span>
                              <div style={{ fontSize: '11px', color: '#8fab9a' }}>
                                Exp: {u.subscription.current_period_end ? new Date(u.subscription.current_period_end * 1000).toLocaleDateString() : 'Lifetime'}
                              </div>
                            </div>
                          ) : (
                            <span style={{ fontSize: '12px', color: '#4e6b5a' }}>No Plan</span>
                          )}
                        </div>

                        {/* Action */}
                        <div>
                          <button
                            onClick={() => {
                              setEditingUserSub(u);
                              setEditSubForm({
                                planId: u.subscription?.plan_id || 'pro',
                                status: u.subscription?.status || 'active',
                                durationDays: '30'
                              });
                            }}
                            style={{ padding: '6px 12px', borderRadius: '8px', background: 'rgba(0, 255, 135, 0.08)', border: '1px solid rgba(0, 255, 135, 0.2)', color: '#00ff87', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                          >
                            Manage
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}

            {/* Inline subscription editing modal */}
            {editingUserSub && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '24px' }}>
                <div style={{ background: '#041008', border: '1px solid rgba(0, 255, 135, 0.15)', borderRadius: '20px', padding: '32px', width: '100%', maxWidth: '440px', position: 'relative' }}>
                  <button onClick={() => setEditingUserSub(null)} style={{ position: 'absolute', top: '20px', right: '20px', background: 'none', border: 'none', color: '#4e6b5a', cursor: 'pointer' }}><Icon.X /></button>
                  <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#f0f0ff', marginBottom: '6px' }}>Manage Subscription</h3>
                  <p style={{ fontSize: '13px', color: '#8fab9a', marginBottom: '24px' }}>Editing subscription status for: <strong style={{ color: '#00ff87' }}>{editingUserSub.email}</strong></p>

                  <form onSubmit={onUpdateSubscriptionSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#8fab9a', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Plan Tier</label>
                      <select className="field-input" value={editSubForm.planId} onChange={e => setEditSubForm(p => ({ ...p, planId: e.target.value }))}>
                        <option value="starter">Starter Plan (₹299/mo)</option>
                        <option value="pro">Professional Plan (₹699/mo)</option>
                        <option value="enterprise">Enterprise Plan (₹1999/mo)</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#8fab9a', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Subscription Status</label>
                      <select className="field-input" value={editSubForm.status} onChange={e => setEditSubForm(p => ({ ...p, status: e.target.value }))}>
                        <option value="active">Active / Paid</option>
                        <option value="expired">Expired / Suspended</option>
                      </select>
                    </div>

                    {editSubForm.status === 'active' && (
                      <div>
                        <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#8fab9a', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Subscription Duration (Days)</label>
                        <select className="field-input" value={editSubForm.durationDays} onChange={e => setEditSubForm(p => ({ ...p, durationDays: e.target.value }))}>
                          <option value="30">30 Days (1 Month)</option>
                          <option value="90">90 Days (3 Months)</option>
                          <option value="180">180 Days (6 Months)</option>
                          <option value="365">365 Days (1 Year)</option>
                          <option value="3650">3650 Days (Lifetime)</option>
                        </select>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                      <button type="button" onClick={() => setEditingUserSub(null)} style={{ flex: 1, padding: '12px', borderRadius: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#8fab9a', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                        Cancel
                      </button>
                      <button type="submit" style={{ flex: 2, padding: '12px', borderRadius: '10px', background: 'linear-gradient(135deg, #00ff87, #059669)', border: 'none', color: '#020603', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        Save Subscription
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CUSTOMER REVIEW PAGE
// ─────────────────────────────────────────────────────────────────────────────
const CustomerReviewPage = ({ backendUrl }) => {
  const searchParams = new URLSearchParams(window.location.search);
  const clientId = searchParams.get('id');
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [businessName, setBusinessName] = useState('Our Business');
  const [businessConfig, setBusinessConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    fetch(`${backendUrl}/api/client/config?id=${clientId}`)
      .then(r => r.json())
      .then(d => { setBusinessName(d.businessName || 'Our Business'); setBusinessConfig(d); })
      .catch(() => {});
  }, [clientId]);

  const handleRatingSelect = async (star) => {
    if (star === 5) {
      try {
        await fetch(`${backendUrl}/api/v1/reviews/submit`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clientId, rating: 5 })
        });
      } catch {}
      window.location.href = businessConfig?.googleReviewUrl || 'https://google.com';
    } else {
      setRating(star);
    }
  };

  const handleComplaintSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await fetch(`${backendUrl}/api/v1/reviews/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId, rating,
          customerName: e.target.customerName.value,
          customerPhone: e.target.customerPhone.value,
          complaintText: e.target.complaintText.value,
        })
      });
      setSubmitted(true);
      setTimeout(() => window.close(), 3500);
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', background: 'radial-gradient(ellipse at center, #052e16 0%, #0a0a14 70%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ textAlign: 'center', animation: 'fadeInUp 0.5s ease' }}>
          <style>{`@keyframes fadeInUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}} @keyframes checkmark{from{stroke-dashoffset:100}to{stroke-dashoffset:0}}`}</style>
          <div style={{ width: '80px', height: '80px', background: 'rgba(52,211,153,0.1)', border: '2px solid rgba(52,211,153,0.3)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: '32px' }}>✓</div>
          <h2 style={{ fontSize: '26px', fontWeight: '800', color: '#f0f0ff', margin: '0 0 10px 0', letterSpacing: '-0.02em' }}>Thank You!</h2>
          <p style={{ color: '#9090b0', fontSize: '15px', lineHeight: 1.6, margin: '0 0 8px 0', maxWidth: '320px' }}>Your feedback has been received privately. The team will reach out to make it right.</p>
          <p style={{ color: '#60607a', fontSize: '13px' }}>This window will close automatically…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'radial-gradient(ellipse at 30% 30%, rgba(99,102,241,0.12) 0%, transparent 55%), radial-gradient(ellipse at 70% 70%, rgba(139,92,246,0.08) 0%, transparent 50%), #0a0a14', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: 'Inter, sans-serif' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes fadeInUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}} .star-btn{font-size:40px;background:none;border:none;cursor:pointer;transition:all 0.15s ease;opacity:0.35;filter:grayscale(1);line-height:1;padding:4px;display:inline-block} .star-btn:hover,.star-btn.hovered,.star-btn.selected{opacity:1;filter:grayscale(0);transform:scale(1.25)} .review-field{width:100%;background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.08);border-radius:10px;padding:11px 14px;color:#f0f0ff;font-size:14px;font-family:'Inter',sans-serif;outline:none;transition:all 0.2s;box-sizing:border-box;resize:none} .review-field:focus{border-color:rgba(99,102,241,0.5);background:rgba(99,102,241,0.05);box-shadow:0 0 0 3px rgba(99,102,241,0.1)} .review-field::placeholder{color:#60607a}`}</style>
      <div style={{ width: '100%', maxWidth: '440px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '24px', animation: 'fadeInUp 0.3s ease' }}>
          <div style={{ width: '60px', height: '60px', margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LogoIcon size={60} />
          </div>
          <h1 style={{ fontSize: '22px', fontWeight: '800', color: '#f0f0ff', margin: '0 0 5px 0', letterSpacing: '-0.02em' }}>{businessName}</h1>
          <p style={{ color: '#9090b0', fontSize: '14px', margin: 0 }}>How was your experience today?</p>
        </div>

        <div style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '20px', padding: '32px', animation: 'fadeInUp 0.4s ease', backdropFilter: 'blur(10px)' }}>
          {rating === 0 ? (
            // Star selection
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: '#9090b0', fontSize: '14px', marginBottom: '24px', marginTop: 0 }}>Tap a star to rate your visit</p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    className={`star-btn ${hovered >= star ? 'hovered' : ''}`}
                    onMouseEnter={() => setHovered(star)}
                    onMouseLeave={() => setHovered(0)}
                    onClick={() => handleRatingSelect(star)}
                  >
                    ⭐
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#60607a', marginTop: '8px' }}>
                <span>Poor</span>
                <span>Excellent</span>
              </div>

              <div style={{ marginTop: '24px', padding: '14px', borderRadius: '12px', background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.12)', textAlign: 'left', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '18px', flexShrink: 0 }}>💬</span>
                <div style={{ fontSize: '13px', color: '#9090b0', lineHeight: 1.5 }}>
                  <strong style={{ color: '#c0c0d8', display: 'block', marginBottom: '2px' }}>5 stars?</strong>
                  You'll be directed to share your review on Google.
                </div>
              </div>
            </div>
          ) : (
            // Complaint form
            <div style={{ animation: 'fadeInUp 0.3s ease' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#f0f0ff', margin: '0 0 3px 0' }}>We'd love to make it right</h3>
                  <p style={{ color: '#9090b0', fontSize: '13px', margin: 0, lineHeight: 1.4 }}>{businessConfig?.sorryMessage || 'Your feedback goes directly to the manager'}</p>
                </div>
                <button onClick={() => setRating(0)} style={{ padding: '6px 10px', borderRadius: '8px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', color: '#9090b0', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {'←'} Back
                </button>
              </div>

              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', borderRadius: '20px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', fontSize: '13px', color: '#f87171', fontWeight: '600', marginBottom: '20px' }}>
                {Array.from({ length: rating }, (_, i) => <span key={i}>⭐</span>)} rating
              </div>

              <form onSubmit={handleComplaintSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#9090b0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Your Name</label>
                  <input className="review-field" name="customerName" type="text" placeholder="John Doe" required />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#9090b0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Phone <span style={{ fontWeight: '400', color: '#60607a' }}>(optional)</span></label>
                  <input className="review-field" name="customerPhone" type="tel" placeholder="9876543210" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#9090b0', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>What happened?</label>
                  <textarea className="review-field" name="complaintText" rows="3" placeholder="Tell us what went wrong so we can improve..." required></textarea>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  style={{ width: '100%', padding: '13px', borderRadius: '12px', background: submitting ? 'rgba(99,102,241,0.5)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', color: 'white', fontSize: '15px', fontWeight: '600', cursor: submitting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 15px rgba(99,102,241,0.3)' }}
                >
                  {submitting ? <Spinner size={18} /> : null}
                  {submitting ? 'Sending...' : '📨 Send Feedback Privately'}
                </button>

                <p style={{ textAlign: 'center', fontSize: '12px', color: '#60607a', margin: '2px 0 0 0' }}>
                  🔒 Your feedback is 100% confidential
                </p>
              </form>
            </div>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          <span style={{ fontSize: '11px', color: '#3a3a4a' }}>Powered by</span>
          <LogoWordmark height={16} />
        </div>
      </div>
    </div>
  );
};

export default ShieldNueraApp;
